<template>
  <div class="public-trips">
    <!-- Hero Section -->
    <section class="hero-section">
      <!-- Header contents at the top of hero section -->
      <div class="hero-header">
        <div class="hero-navbar-content">
          <!-- Left Side: Brand and Navigation -->
          <div
            class="hero-left-section"
            style="
              display: flex;
              align-items: center;
              gap: 2rem;
              flex: 0 0 auto;
              margin-left: 0;
            "
          >
            <router-link class="hero-brand" to="/trips">
              <img
                src="@/assets/logo.jpg"
                alt="Gypsy Souls"
                class="hero-logo"
              />
            </router-link>

            <!-- Navigation Menu -->
            <div class="hero-nav-menu">
              <ul v-if="isAuthenticated" class="hero-nav-list">
                <li class="hero-nav-item">
                  <a href="#" @click="scrollToPhilosophy" class="hero-nav-link">
                    About Us
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <!-- Right Side: Phone and User Menu -->
          <div
            class="hero-right-section"
            style="
              display: flex;
              align-items: center;
              gap: 2rem;
              flex: 0 0 auto;
              margin-right: 0;
            "
          >
            <!-- Phone Contact -->
            <div class="hero-phone">
              <a href="tel:+918870024298" class="hero-phone-link">
                <i class="fas fa-phone me-1"></i>
                +91 88700 24298
              </a>
            </div>

            <!-- User Menu -->
            <div class="hero-user-menu">
              <!-- User Authentication -->
              <div v-if="!isAuthenticated" class="hero-auth-links">
                <router-link to="/login" class="hero-auth-link">
                  <i class="fas fa-sign-in-alt me-1"></i>
                  Login
                </router-link>
                <router-link to="/signup" class="hero-auth-link">
                  <i class="fas fa-user-plus me-1"></i>
                  Sign Up
                </router-link>
              </div>

              <!-- User Dropdown -->
              <div v-if="isAuthenticated" class="hero-user-dropdown">
                <button
                  class="hero-user-btn"
                  @click="toggleUserDropdown"
                  :class="{ active: showUserDropdown }"
                >
                  <i class="fas fa-user me-1"></i>
                  {{
                    currentUser?.username || currentUser?.mobileNumber || "User"
                  }}
                  <i class="fas fa-chevron-down ms-1"></i>
                </button>
                <div v-if="showUserDropdown" class="hero-dropdown-menu">
                  <a href="#" @click="logout" class="hero-dropdown-item">
                    <i class="fas fa-sign-out-alt me-1"></i>
                    Logout
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="hero-background-animation"></div>
      <div class="hero-content">
        <div class="hero-title">
          <span class="hero-main-text">Welcome to</span>
          <span class="hero-accent-text">GypsySouls</span>
        </div>
        <div class="hero-second-tagline">
          <span class="second-tagline-text"
            >For travelers, By travelers</span
          >
        </div>
        <p class="hero-subtitle">Let your spirit roam with us</p>
        <div class="hero-stats">
          <div class="stat-item">
            <div class="stat-number">{{ statistics.amazingTrips }}</div>
            <div class="stat-label">Amazing Trips</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ statistics.happyTravelers }}</div>
            <div class="stat-label">Happy Travelers</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ statistics.destinations }}+</div>
            <div class="stat-label">Destinations</div>
          </div>
        </div>

        <!-- Rating Icons -->
        <div class="hero-ratings" @click="redirectToGoogleReviews" title="Click to view Google Reviews">
          <div class="rating-icon combined-rating">
            <div class="rating-platforms">
              <div class="platform-icon google-icon">
                <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              </div>
            </div>
            <div class="rating-stars">
              <i class="fas fa-star"></i>
              <i class="fas fa-star"></i>
              <i class="fas fa-star"></i>
              <i class="fas fa-star"></i>
              <i class="fas fa-star"></i>
            </div>
            <div class="rating-text">5.0</div>
          </div>
        </div>

        <!-- Hero Action Buttons -->
        <div class="hero-actions">
          <button class="hero-btn primary" @click="scrollToTrips">
            <i class="fas fa-globe-americas"></i>
            Start Exploring
          </button>
          <button class="hero-btn customize" @click="customizeTrip()">
            <i class="fas fa-magic"></i>
            Customize Your Trip
          </button>
        </div>
      </div>
    </section>

    <!-- Trips Section -->
    <section class="trips-section" ref="tripsSection">
      <!-- Background Image -->
      <div class="section-background"></div>
      <div class="container">
        <div class="section-header">
          <div class="section-icon-wrapper">
            <div class="section-icon">
              <i class="fas fa-globe-americas"></i>
            </div>
          </div>
          <h2 class="section-title">
            <span class="title-accent">Featured</span>
            <span class="title-accent">Destinations</span>
          </h2>
          <p class="section-subtitle">
            Handpicked adventures that will take your breath away
          </p>
          <div class="section-decoration">
            <div class="decoration-line"></div>
            <div class="decoration-dot"></div>
            <div class="decoration-line"></div>
          </div>
        </div>

        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading amazing adventures...</p>
        </div>

        <div v-else-if="error" class="error-container">
          <div class="error-icon">⚠️</div>
          <h3>Oops! Something went wrong</h3>
          <p>{{ error }}</p>
          <button @click="loadTrips" class="retry-btn">Try Again</button>
        </div>

        <div v-else-if="trips.length === 0" class="empty-state">
          <div class="empty-icon">🏔️</div>
          <h3>No trips available</h3>
          <p>Check back soon for amazing adventures!</p>
        </div>

        <div v-else>
          <div class="trips-grid">
            <div
              v-for="trip in displayedTrips"
              :key="trip.id"
              class="trip-card animate-fade-in-up"
            >
            <!-- Image/Video Carousel -->
            <div class="carousel-container">
              <div class="carousel-content">
                <!-- Show current media based on index -->
                <video
                  v-if="isCurrentMediaVideo(trip)"
                  :src="getCurrentMediaUrl(trip)"
                  class="carousel-video"
                  muted
                  loop
                  autoplay
                  playsinline
                  @click="openMedia(trip)"
                />
                <img
                  v-else-if="isCurrentMediaImage(trip)"
                  :src="getCurrentMediaUrl(trip)"
                  :alt="trip.place || 'Trip image'"
                  class="carousel-image"
                  @click="openMedia(trip)"
                />
                <!-- Show placeholder if no media -->
                <div v-else class="no-media-placeholder">
                  <i class="fas fa-image"></i>
                  <span>No media available</span>
                </div>
              </div>
            </div>

            <!-- Card Body -->
            <div class="card-body">
              <!-- Trip Title -->
              <div class="trip-title">
                <div class="location-icon">
                  <i class="fas fa-map-marker-alt"></i>
                </div>
                <h3 class="location-name">
                  {{ trip.place || "Unknown Location" }}
                </h3>
              </div>

              <!-- View Itinerary Button -->
              <div class="trip-itinerary-button-container">
                <button
                  @click="viewItinerary(trip)"
                  class="view-itinerary-btn-compact"
                  :title="
                    'View Itinerary for ' +
                    trip.place +
                    ' (PDF: ' +
                    (trip.itineraryPdf || 'Not available') +
                    ')'
                  "
                  type="button"
                >
                  <i class="fas fa-file-alt"></i>
                  View Itinerary
                </button>
              </div>

              <!-- Trip Pricing Row -->
              <div class="trip-pricing-row">
                <div class="price-display">
                  <span class="starts-from">Starts from</span>
                  <span class="currency">₹</span>
                  <span class="amount">{{ trip.price || 0 }}</span>
                  <span class="per-person">per person</span>
                </div>
              </div>

              <!-- Trip Duration and Group Size Row -->
              <div class="trip-duration-group-row">
                <div class="duration-display">
                  <span class="duration-text">{{ trip.days || 'Flexible' }}</span>
                </div>
                <div class="group-size-display">
                  <span class="group-size-text">{{ getGroupSizeDisplay(trip) }}</span>
                </div>
              </div>

              <!-- Trip Dates Row -->
              <div class="trip-dates-row">
                <div class="meta-item">
                  <i class="fas fa-calendar-alt"></i>
                  <span>{{ getTripDateDisplay(trip) }}</span>
                </div>
              </div>

              <!-- Trip Description -->
              <div class="trip-description">
                <p class="description-text">
                  {{ trip.description || "No description available" }}
                </p>
              </div>

              <!-- Action Buttons -->
              <div
                class="action-buttons-container"
                style="
                  display: flex;
                  gap: 10px;
                  margin-top: 16px;
                  padding: 0;
                  flex-wrap: nowrap;
                  position: relative;
                  z-index: 50;
                  pointer-events: auto;
                  align-items: stretch;
                "
              >
                <!-- Book Now Button -->
                <button
                  @click="bookNow(trip)"
                  class="book-now-btn"
                  style="
                    background: linear-gradient(
                      135deg,
                      #ff8c42 0%,
                      #d4a574 100%
                    );
                    color: white;
                    padding: 6px 12px;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 9px;
                    font-weight: 600;
                    min-width: 90px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                    position: relative;
                    z-index: 100;
                    pointer-events: auto;
                    flex: 1 1 0;
                    min-height: 32px;
                    max-height: 32px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  "
                  type="button"
                >
                  <i
                    class="fas fa-bookmark"
                    style="margin-right: 4px; font-size: 9px; flex-shrink: 0"
                  ></i>
                  <span
                    style="
                      flex-shrink: 1;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                    >Book Now</span
                  >
                </button>

                <!-- Customize Trip Button -->
                <button
                  @click="customizeTrip(trip)"
                  class="customize-trip-btn"
                  style="
                    background: linear-gradient(
                      135deg,
                      #96323c 0%,
                      #5a6446 100%
                    );
                    color: white;
                    padding: 6px 12px;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 9px;
                    font-weight: 600;
                    min-width: 90px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                    position: relative;
                    z-index: 100;
                    pointer-events: auto;
                    flex: 1 1 0;
                    min-height: 32px;
                    max-height: 32px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  "
                  type="button"
                >
                  <i
                    class="fas fa-magic"
                    style="margin-right: 4px; font-size: 9px; flex-shrink: 0"
                  ></i>
                  <span
                    style="
                      flex-shrink: 1;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                    >Customize</span
                  >
                </button>
              </div>
            </div>
          </div>
          </div>

          <!-- Show More Button -->
          <div v-if="hasMoreTrips" class="show-more-container">
            <button
              @click.stop="toggleShowAllTrips"
              class="show-more-btn"
              type="button"
            >
              <span v-if="!showAllTripsMobile">
                <i class="fas fa-chevron-down me-2"></i>
                Show More ({{ hiddenTripsCount }} more)
              </span>
              <span v-else>
                <i class="fas fa-chevron-up me-2"></i>
                Show Less
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Reviews Section -->
    <section class="reviews-section" ref="reviewsSection">
      <!-- Background Image -->
      <div class="section-background"></div>
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            <span class="title-accent">Traveler</span>
            <span class="title-accent">Reviews</span>
          </h2>
          <p class="section-subtitle">
            See what our adventurers have to say about their experiences
          </p>
        </div>

        <div v-if="reviewsLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading reviews...</p>
        </div>

        <div v-else-if="reviewsError" class="error-container">
          <div class="error-icon">⚠️</div>
          <h3>Couldn't load reviews</h3>
          <p>{{ reviewsError }}</p>
        </div>

        <div v-else-if="reviews.length === 0" class="empty-state">
          <div class="empty-icon">📝</div>
          <h3>No reviews yet</h3>
          <p>Be the first to share your experience!</p>
        </div>

        <div
          v-else
          class="reviews-scroll-container"
          ref="reviewsScrollContainer"
          @mouseenter="pauseReviewAutoScroll"
          @mouseleave="resumeReviewAutoScroll"
          @focusin="pauseReviewAutoScroll"
          @focusout="resumeReviewAutoScroll"
        >
          <div class="reviews-scroll-row" ref="reviewsScrollRow">
            <div
              v-for="review in reviews || []"
              :key="review._id"
              class="review-card-wrapper"
            >
              <div
                class="review-card animate-fade-in-up"
                @click="redirectToGoogleReviews"
                @focus="pauseReviewAutoScroll"
                @blur="resumeReviewAutoScroll"
                @mouseenter="pauseReviewAutoScroll"
                @mouseleave="resumeReviewAutoScroll"
                tabindex="0"
                title="Click to view Google Reviews"
              >
                <div class="review-header">
                  <div>
                    <div class="reviewer-name">
                      {{ review.reviewerName || "Anonymous" }}
                    </div>
                    <div class="rating">
                      <i
                        v-for="star in 5"
                        :key="star"
                        :class="[
                          'fas',
                          star <= (review.rating || 0)
                            ? 'fa-star'
                            : 'fa-star-o',
                        ]"
                      ></i>
                    </div>
                  </div>
                </div>

                <!-- Review Text (always show if present) -->
                <div
                  v-if="review.reviewText && review.reviewText.trim()"
                  class="review-text-container"
                >
                  <p class="review-text">
                    {{ review.reviewText }}
                  </p>
                </div>

                <!-- Review Images (show below text if present) -->
                <div
                  v-if="review.images && review.images.length > 0"
                  class="review-media mb-3"
                >
                  <div class="media-scroll-container">
                    <div class="media-scroll-row">
                      <div
                        v-for="(imageUrl, index) in review.images || []"
                        :key="`img-${index}`"
                        class="media-item"
                        @click.stop="openMediaInNewTab(imageUrl)"
                      >
                        <img
                          :src="imageUrl"
                          :alt="`Review image ${index + 1}`"
                          class="media-thumbnail"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Review Video (show below text and images if present) -->
                <div v-if="review.video" class="review-media mb-3">
                  <div class="media-scroll-container">
                    <div class="media-scroll-row">
                      <div
                        class="media-item"
                        @click.stop="openMediaInNewTab(review.video)"
                      >
                        <video
                          :src="review.video"
                          class="media-thumbnail"
                          muted
                        ></video>
                        <!-- Video play indicator -->
                        <div class="video-play-overlay">
                          <i class="fas fa-play"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="review.trip" class="review-trip">
                  <strong>Trip:</strong>
                  {{ review.trip.place || "Unknown Trip" }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- Philosophy Section -->
    <section class="philosophy-section">
      <div class="container">
        <div class="philosophy-card">
          <div class="philosophy-icon">
            <i class="fas fa-heart"></i>
          </div>
          <h2 class="philosophy-title">
            Travel isn't a destination; it's a state of soul
          </h2>
          <div class="philosophy-text">
            <div class="philosophy-section-content">
              <h3 class="philosophy-subtitle">Why choose Gypsy Souls?</h3>
              <p>
                At Gypsy Souls, we believe in more than just ticking destinations
                off a map. We're about chasing sunsets, finding hidden gems and
                connecting with local cultures in its rawest form.
              </p>
            </div>

            <div class="philosophy-section-content">
              <h3 class="philosophy-subtitle">Whom are our tours for?</h3>
              <p>
                Whether you're a retiree ready to explore, a solo traveler craving
                connection, a couple chasing romance or a team looking for the
                perfect corporate escape or a tribe of friends—we've got you
                covered.
              </p>
            </div>

            <div class="philosophy-section-content">
              <h3 class="philosophy-subtitle">What makes GYPSY SOULS stand out?</h3>
              <p>
                Our tours are women-safe, stranger-to-friend friendly, and
                designed for every kind of explorer from quick breaks to
                bucket-list international adventures. Let's turn your travel
                dreams into unforgettable journeys!
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Custom Journeys Section -->
    <section class="custom-journeys-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            <span class="title-accent">Custom Journeys.</span>
            <span class="title-accent">Global Routes.</span>
          </h2>
          <p class="section-subtitle">
            Discover our diverse range of travel experiences
          </p>
        </div>

        <div class="journey-grid">
          <div class="journey-card" data-journey="wild">
            <div class="journey-icon">🌿</div>
            <h4 class="journey-name">Wild Wanderings</h4>
            <p class="journey-description">Safaris, hikes, eco-escapes</p>
            <div class="journey-overlay">
              <span class="journey-tag">Adventure</span>
            </div>
          </div>

          <div class="journey-card" data-journey="urban">
            <div class="journey-icon">🏙️</div>
            <h4 class="journey-name">Urban Escapes</h4>
            <p class="journey-description">Culture-rich city stays</p>
            <div class="journey-overlay">
              <span class="journey-tag">Culture</span>
            </div>
          </div>

          <div class="journey-card" data-journey="sisterhood">
            <div class="journey-icon">👭</div>
            <h4 class="journey-name">She Travels</h4>
            <p class="journey-description">
              Safe, Soulful & Full of sisterhood
            </p>
            <div class="journey-overlay">
              <span class="journey-tag">Sisterhood</span>
            </div>
          </div>

          <div class="journey-card" data-journey="weekend">
            <div class="journey-icon">⏰</div>
            <h4 class="journey-name">Soulful Weekenders</h4>
            <p class="journey-description">Quick getaways for busy dreamers</p>
            <div class="journey-overlay">
              <span class="journey-tag">Quick Escape</span>
            </div>
          </div>

          <div class="journey-card" data-journey="mystic">
            <div class="journey-icon">🧘</div>
            <h4 class="journey-name">Mystic Trails</h4>
            <p class="journey-description">
              Sacred sites, yoga retreats, and spiritual Journey
            </p>
            <div class="journey-overlay">
              <span class="journey-tag">Spiritual</span>
            </div>
          </div>

          <div class="journey-card" data-journey="thematic">
            <div class="journey-icon">📸</div>
            <h4 class="journey-name">Thematic Tours</h4>
            <p class="journey-description">
              Photography Trials, Food & Architecture Explores
            </p>
            <div class="journey-overlay">
              <span class="journey-tag">Specialized</span>
            </div>
          </div>
        </div>

        <div class="journey-highlights">
          <div class="highlight-item">
            <i class="fas fa-users"></i>
            <span>Small group sizes</span>
          </div>
          <div class="highlight-item">
            <i class="fas fa-leaf"></i>
            <span>Eco-conscious stays</span>
          </div>
          <div class="highlight-item">
            <i class="fas fa-map-marked-alt"></i>
            <span>Authentic local guides</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Customization Section -->
    <section id="customization-section" class="customization-section">
      <div class="container">
        <div class="customization-content">
          <div class="customization-icon-wrapper">
            <div class="customization-icon">
              <i class="fas fa-magic"></i>
            </div>
          </div>
          <div class="customization-title">
            <span class="title-main">Design Your Perfect</span>
            <span class="title-accent">Adventure</span>
          </div>
          <p class="customization-subtitle">
            Can't find exactly what you're looking for? Let us create a custom
            trip just for you!
          </p>
          <div class="customization-features">
            <div class="feature-item">
              <i class="fas fa-route"></i>
              <span>Custom Itineraries</span>
            </div>
            <div class="feature-item">
              <i class="fas fa-calendar-check"></i>
              <span>Flexible Dates</span>
            </div>
            <div class="feature-item">
              <i class="fas fa-users"></i>
              <span>Group Sizes</span>
            </div>
            <div class="feature-item">
              <i class="fas fa-crown"></i>
              <span>Premium Experiences</span>
            </div>
          </div>
          <div class="customization-cta">
            <button @click="customizeTrip()" class="customization-btn">
              Start Customizing
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Customization Form Modal -->
    <div
      v-if="showCustomizationForm"
      class="customization-modal-overlay"
      @click="closeCustomizationForm"
    >
      <div class="customization-modal" @click.stop>
        <div class="modal-header">
          <h3>Customize Your Perfect Adventure</h3>
          <button @click="closeCustomizationForm" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form
            @submit.prevent="submitCustomizationForm"
            class="customization-form"
          >
            <div class="form-group">
              <label for="preferredPlace"
                >Preferred Destination <span class="required">*</span></label
              >
              <input
                type="text"
                id="preferredPlace"
                v-model="customizationForm.preferredPlace"
                class="form-control"
                placeholder="e.g., Kerala, Goa, Himachal Pradesh"
                required
              />
            </div>

            <div class="form-group">
              <label for="numberOfMembers"
                >Number of Members <span class="required">*</span></label
              >
              <input
                type="number"
                id="numberOfMembers"
                v-model.number="customizationForm.numberOfMembers"
                class="form-control"
                placeholder="e.g., 2, 4, 6"
                min="1"
                max="50"
                required
              />
            </div>

            <!-- Mandatory Date Fields -->
            <div class="form-row">
              <div class="form-group col-md-6">
                <label for="startDate">Start Date <span class="required">*</span></label>
                <input
                  type="date"
                  id="startDate"
                  v-model="customizationForm.startDate"
                  class="form-control"
                  :min="new Date().toISOString().split('T')[0]"
                  required
                />
              </div>
              <div class="form-group col-md-6">
                <label for="endDate">End Date <span class="required">*</span></label>
                <input
                  type="date"
                  id="endDate"
                  v-model="customizationForm.endDate"
                  class="form-control"
                  :min="customizationForm.startDate || new Date().toISOString().split('T')[0]"
                  required
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group col-md-6">
                <label for="numberOfAdults">Number of Adults (Optional)</label>
                <input
                  type="number"
                  id="numberOfAdults"
                  v-model.number="customizationForm.numberOfAdults"
                  class="form-control"
                  placeholder="e.g., 2, 4"
                  min="0"
                  max="50"
                />
              </div>
              <div class="form-group col-md-6">
                <label for="numberOfKids">Number of Kids (Optional)</label>
                <input
                  type="number"
                  id="numberOfKids"
                  v-model.number="customizationForm.numberOfKids"
                  class="form-control"
                  placeholder="e.g., 1, 2"
                  min="0"
                  max="20"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="tripType">Type of Trip (Optional)</label>
              <select
                id="tripType"
                v-model="customizationForm.tripType"
                class="form-control"
              >
                <option value="">Select trip type</option>
                <option value="family-trip">Family Trip</option>
                <option value="friends-trip">Friends Trip</option>
                <option value="womens-trip">Women's Trip</option>
                <option value="honeymoon-trip">Honeymoon Trip</option>
                <option value="adventure-trip">Adventure Trip</option>
                <option value="corporate">Corporate</option>
              </select>
            </div>

            <div class="form-group">
              <label for="additionalNotes">Additional Notes (Optional)</label>
              <textarea
                id="additionalNotes"
                v-model="customizationForm.additionalNotes"
                class="form-control"
                rows="3"
                placeholder="Tell us more about your dream trip..."
              ></textarea>
            </div>

            <div class="form-actions">
              <button
                type="button"
                @click="closeCustomizationForm"
                class="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="isSubmitting"
              >
                <i v-if="isSubmitting" class="fas fa-spinner fa-spin"></i>
                {{ isSubmitting ? "Sending..." : "Send Request" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Why Travel With Us Section -->
    <section class="why-travel-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            <span class="title-accent">Discover what makes our</span>
            <span class="title-accent">journeys extraordinary</span>
          </h2>
        </div>

        <div class="why-travel-grid">
          <div class="why-card" data-benefit="stays">
            <div class="why-icon">
              <i class="fas fa-home"></i>
            </div>
            <div class="why-content">
              <h3 class="why-title">Handpicked Stays</h3>
              <p class="why-description">
                Unique accommodations with local charm that you won't find in
                any brochure
              </p>
            </div>
          </div>

          <div class="why-card" data-benefit="connection">
            <div class="why-icon">
              <i class="fas fa-handshake"></i>
            </div>
            <div class="why-content">
              <h3 class="why-title">Cultural Connection</h3>
              <p class="why-description">
                Experience life with locals, not from the sidelines. Authentic
                interactions await
              </p>
            </div>
          </div>

          <div class="why-card" data-benefit="gems">
            <div class="why-icon">
              <i class="fas fa-gem"></i>
            </div>
            <div class="why-content">
              <h3 class="why-title">Hidden Gems</h3>
              <p class="why-description">
                We go beyond the brochure to show you places only locals know
                about
              </p>
            </div>
          </div>

          <div class="why-card" data-benefit="planning">
            <div class="why-icon">
              <i class="fas fa-clipboard-check"></i>
            </div>
            <div class="why-content">
              <h3 class="why-title">Worry-Free Planning</h3>
              <p class="why-description">
                We handle all logistics. You just roam and create unforgettable
                memories
              </p>
            </div>
          </div>

          <div class="why-card" data-benefit="sustainable">
            <div class="why-icon">
              <i class="fas fa-globe-americas"></i>
            </div>
            <div class="why-content">
              <h3 class="why-title">Sustainable Soul</h3>
              <p class="why-description">
                Travel that gives back to people & planet. Responsible tourism
                at its finest
              </p>
            </div>
          </div>

          <div class="why-card" data-benefit="safety">
            <div class="why-icon">
              <i class="fas fa-shield-alt"></i>
            </div>
            <div class="why-content">
              <h3 class="why-title">Women-Safe Travel</h3>
              <p class="why-description">
                Safe, soulful journeys designed with women travelers in mind
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>


    <!-- Fixed Review Icon -->
    <div class="fixed-review-icon" @click="scrollToReviews">
      <i class="fas fa-star"></i>
      <span class="review-icon-text">Reviews</span>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";

export default {
  name: "PublicTrips",
  setup() {
    const router = useRouter();
    const store = useStore();
    const trips = ref([]);
    const reviews = ref([]);
    const loading = ref(true);
    const reviewsLoading = ref(true);
    const error = ref(null);
    const reviewsError = ref(null);
    const showAllTripsMobile = ref(false);
    const isMobile = ref(false);
    const isTablet = ref(false);

    // Check screen size on mount and window resize
    const checkScreenSize = () => {
      const width = window.innerWidth;
      isMobile.value = width <= 768;
      isTablet.value = width > 768 && width <= 992;
    };

    // Admin-configurable statistics
    const statistics = ref({
      amazingTrips: 7,
      happyTravelers: 8,
      destinations: 50,
    });

    // Load statistics from backend
    const loadStatistics = async () => {
      try {
        const response = await axios.get('/statistics');
        if (response.data.success) {
          statistics.value = response.data.statistics;
        }
      } catch (error) {
        console.error("Error loading statistics:", error);
        // Keep default values if API fails
      }
    };

    // Save statistics to backend (admin only)
    const saveStatistics = async (newStats) => {
      try {
        const response = await axios.put('/statistics', newStats, {
          headers: {
            'Authorization': `Bearer ${store.getters['auth/authToken']}`
          }
        });
        if (response.data.success) {
          statistics.value = response.data.statistics;
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error saving statistics:", error);
        return false;
      }
    };

    // Admin panel state
    const currentMediaIndex = ref({});
    const autoAdvanceTimers = ref({});
    const tripsSection = ref(null);
    const reviewsSection = ref(null);
    const reviewsScrollContainer = ref(null);
    const reviewsScrollRow = ref(null);
    const reviewScrollTimer = ref(null);
    const isReviewFocused = ref(false);

    // Customization form state
    const showCustomizationForm = ref(false);
    const isSubmitting = ref(false);
    const currentCustomizingTrip = ref(null);
    const customizationForm = ref({
      preferredPlace: "",
      numberOfMembers: null,
      startDate: "",
      endDate: "",
      numberOfAdults: null,
      numberOfKids: null,
      tripType: "",
      additionalNotes: "",
    });

    // Auto-popup timer
    const autoPopupTimer = ref(null);
    const hasShownAutoPopup = ref(false);

    // User dropdown state
    const showUserDropdown = ref(false);

    // Get current user information
    const currentUser = computed(() => store.getters["auth/currentUser"]);
    const isAuthenticated = computed(
      () => store.getters["auth/isAuthenticated"]
    );
    const userType = computed(() => store.getters["auth/userType"]);

    // Format date function
    const formatDate = (dateString) => {
      return moment(dateString).format("MMM DD, YYYY");
    };

    // Get trip date display
    const getTripDateDisplay = (trip) => {
      if (trip.startDate && trip.endDate) {
        const startDate = moment(trip.startDate).format("MMM DD, YYYY");
        const endDate = moment(trip.endDate).format("MMM DD, YYYY");
        return `${startDate} - ${endDate}`;
      } else if (trip.startDate && !trip.endDate) {
        const startDate = moment(trip.startDate).format("MMM DD, YYYY");
        return `${startDate} - Flexible end date`;
      } else if (!trip.startDate && trip.endDate) {
        const endDate = moment(trip.endDate).format("MMM DD, YYYY");
        return `Flexible start date - ${endDate}`;
      } else {
        return 'Flexible dates';
      }
    };

    // Get group size display
    const getGroupSizeDisplay = (trip) => {
      if (trip.groupSize && trip.groupSize > 0) {
        return `${trip.groupSize} Members`;
      } else {
        return 'Flexible Group Size';
      }
    };

    // Load trips from API
    const loadTrips = async () => {
      try {
        loading.value = true;
        error.value = null;
        const response = await axios.get("/public/trips");

        if (
          response.data &&
          response.data.success &&
          Array.isArray(response.data.trips)
        ) {
          // Sort trips by updatedAt (newest first)
          const sortedTrips = response.data.trips.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return dateB - dateA; // Descending order (newest first)
          });
          trips.value = sortedTrips;

          // Initialize media index for each trip
          trips.value.forEach((trip) => {
            currentMediaIndex.value[trip.id] = 0;
          });

          // Start auto-advance for carousels
          try {
            startAutoAdvance();
          } catch (autoAdvanceErr) {
            console.error("Error starting auto-advance:", autoAdvanceErr);
          }
        } else {
          error.value = "Invalid data format received from server.";
        }
      } catch (err) {
        console.error("Error loading trips:", err);
        error.value = "Failed to load trips. Please try again later.";
      } finally {
        loading.value = false;
      }
    };

    // Load reviews from API
    const loadReviews = async () => {
      try {
        reviewsLoading.value = true;
        reviewsError.value = null;
        const response = await axios.get("/public/reviews");

        if (
          response.data &&
          response.data.success &&
          Array.isArray(response.data.reviews)
        ) {
          reviews.value = response.data.reviews;
        } else if (response.data && Array.isArray(response.data)) {
          // Fallback for direct array response
          reviews.value = response.data;
        } else {
          reviews.value = [];
        }
      } catch (err) {
        console.error("Error loading reviews:", err);
        reviewsError.value = "Failed to load reviews. Please try again later.";
      } finally {
        reviewsLoading.value = false;
      }
    };

    // Media navigation functions
    const nextMedia = (trip) => {
      const currentIndex = currentMediaIndex.value[trip.id] || 0;
      const totalMedia =
        (trip.video ? 1 : 0) + (trip.images ? trip.images.length : 0);

      if (totalMedia === 0) return;

      const nextIndex = (currentIndex + 1) % totalMedia;
      currentMediaIndex.value[trip.id] = nextIndex;
    };

    const previousMedia = (trip) => {
      const currentIndex = currentMediaIndex.value[trip.id] || 0;
      const totalMedia =
        (trip.video ? 1 : 0) + (trip.images ? trip.images.length : 0);

      if (totalMedia === 0) return;

      const prevIndex = currentIndex === 0 ? totalMedia - 1 : currentIndex - 1;
      currentMediaIndex.value[trip.id] = prevIndex;
    };

    const goToMedia = (trip, index) => {
      currentMediaIndex.value[trip.id] = index;
    };

    // Helper functions for media display
    const isCurrentMediaImage = (trip) => {
      const currentIndex = currentMediaIndex.value[trip.id] || 0;
      // If there's a video, images start from index 1 (after video)
      if (trip.video) {
        return trip.images && trip.images[currentIndex - 1];
      }
      // If no video, images start from index 0
      return trip.images && trip.images[currentIndex];
    };

    const isCurrentMediaVideo = (trip) => {
      const currentIndex = currentMediaIndex.value[trip.id] || 0;
      // Video is shown at index 0
      return trip.video && currentIndex === 0;
    };

    const getCurrentMediaUrl = (trip) => {
      const currentIndex = currentMediaIndex.value[trip.id] || 0;

      // Video is at index 0
      if (trip.video && currentIndex === 0) {
        return trip.video;
      }

      // Images come after video
      if (trip.video && trip.images && trip.images[currentIndex - 1]) {
        return trip.images[currentIndex - 1];
      }

      // If no video, images start from index 0
      if (!trip.video && trip.images && trip.images[currentIndex]) {
        return trip.images[currentIndex];
      }

      return null;
    };

    // Auto-advance carousel
    const startAutoAdvance = () => {
      trips.value.forEach((trip) => {
        // Start auto-advance if there are multiple media items (video + images or multiple images)
        const totalMedia =
          (trip.video ? 1 : 0) + (trip.images ? trip.images.length : 0);
        if (totalMedia > 1) {
          autoAdvanceTimers.value[trip.id] = setInterval(() => {
            nextMedia(trip);
          }, 4000);
        }
      });
    };

    const stopAutoAdvance = () => {
      Object.values(autoAdvanceTimers.value).forEach((timer) => {
        clearInterval(timer);
      });
      autoAdvanceTimers.value = {};
    };

    // Action functions
    const bookNow = (trip) => {
      // Redirect to Google Sheet URL from trip data
      if (trip.googleSheetUrl) {
        // Open Google Sheet in new page
        window.open(trip.googleSheetUrl, "_blank");
      } else {
        window.alert("Booking link not available for this trip.");
      }
    };

    const viewItinerary = (trip) => {
      if (trip.itineraryPdf) {
        // Open PDF in new page
        window.open(trip.itineraryPdf, "_blank");
      } else {
        window.alert("Itinerary not available for this trip.");
      }
    };

    const customizeTrip = async (trip = null) => {
      // Check if user is authenticated
      if (!isAuthenticated.value) {
        router.push("/login");
        return;
      }

      // Clear the auto-popup timer since user is manually opening the form
      clearAutoPopupTimer();

      // Store the trip being customized
      currentCustomizingTrip.value = trip;

      // Pre-fill form with trip details if customizing a specific trip
      if (trip) {
        customizationForm.value.preferredPlace = trip.place;
      } else {
        // Reset form for general customization
        customizationForm.value.preferredPlace = "";
      }

      // Open the customization form
      showCustomizationForm.value = true;
    };

    // Customization form methods
    const closeCustomizationForm = () => {
      showCustomizationForm.value = false;
      // Reset form and trip
      currentCustomizingTrip.value = null;
      customizationForm.value = {
        preferredPlace: "",
        numberOfMembers: null,
        startDate: "",
        endDate: "",
        numberOfAdults: null,
        numberOfKids: null,
        tripType: "",
        additionalNotes: "",
      };
    };

    const submitCustomizationForm = async () => {
      if (!isAuthenticated.value) {
        router.push("/login");
        return;
      }

      // Validate required fields
      if (
        !customizationForm.value.preferredPlace ||
        customizationForm.value.preferredPlace.trim() === ""
      ) {
        Swal.fire({
          title: "Required Field Missing",
          text: "Please enter your preferred destination.",
          icon: "warning",
          confirmButtonColor: "#2563eb",
        });
        return;
      }

      if (
        !customizationForm.value.numberOfMembers ||
        customizationForm.value.numberOfMembers < 1
      ) {
        Swal.fire({
          title: "Required Field Missing",
          text: "Please enter the number of members (minimum 1).",
          icon: "warning",
          confirmButtonColor: "#2563eb",
        });
        return;
      }

      if (!customizationForm.value.startDate) {
        Swal.fire({
          title: "Required Field Missing",
          text: "Please select your start date.",
          icon: "warning",
          confirmButtonColor: "#2563eb",
        });
        return;
      }

      if (!customizationForm.value.endDate) {
        Swal.fire({
          title: "Required Field Missing",
          text: "Please select your end date.",
          icon: "warning",
          confirmButtonColor: "#2563eb",
        });
        return;
      }

      // Validate that end date is after start date
      if (new Date(customizationForm.value.endDate) <= new Date(customizationForm.value.startDate)) {
        Swal.fire({
          title: "Invalid Date Range",
          text: "End date must be after start date.",
          icon: "warning",
          confirmButtonColor: "#2563eb",
        });
        return;
      }

      try {
        isSubmitting.value = true;

        // Show loading
        Swal.fire({
          title: "Processing...",
          text: "Sending your customization request",
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Prepare customization data (all fields are now mandatory)
        const customizationData = {
          preferredPlace: customizationForm.value.preferredPlace.trim(),
          numberOfMembers: customizationForm.value.numberOfMembers,
          startDate: customizationForm.value.startDate,
          endDate: customizationForm.value.endDate,
        };

        // Build message (all fields are now mandatory)
        let message = `User ${
          currentUser.value?.username || currentUser.value?.mobileNumber
        } requested for customization with preferences: Place: ${customizationForm.value.preferredPlace.trim()}, Members: ${
          customizationForm.value.numberOfMembers
        }, Start Date: ${customizationForm.value.startDate}, End Date: ${customizationForm.value.endDate}`;

        // Add optional fields to message if provided
        if (customizationForm.value.numberOfAdults) {
          message += `, Adults: ${customizationForm.value.numberOfAdults}`;
        }
        if (customizationForm.value.numberOfKids) {
          message += `, Kids: ${customizationForm.value.numberOfKids}`;
        }
        if (customizationForm.value.tripType) {
          const tripTypeLabels = {
            'family-trip': 'Family Trip',
            'friends-trip': 'Friends Trip',
            'womens-trip': "Women's Trip",
            'honeymoon-trip': 'Honeymoon Trip',
            'adventure-trip': 'Adventure Trip',
            'corporate': 'Corporate'
          };
          message += `, Trip Type: ${tripTypeLabels[customizationForm.value.tripType] || customizationForm.value.tripType}`;
        }
        if (customizationForm.value.additionalNotes) {
          message += ` Additional Notes: ${customizationForm.value.additionalNotes}`;
        }

        let requestData;

        if (currentCustomizingTrip.value) {
          // Customize specific trip - send trip details
          requestData = {
            type: "trip_customization",
            user: {
              username:
                currentUser.value?.username || currentUser.value?.mobileNumber,
              mobileNumber: currentUser.value?.mobileNumber,
              countryCode: currentUser.value?.countryCode,
              email: currentUser.value?.email,
            },
            trip: {
              id: currentCustomizingTrip.value.id,
              name: currentCustomizingTrip.value.place,
              price: currentCustomizingTrip.value.price,
              days: currentCustomizingTrip.value.days,
            },
            customizationData: customizationData,
            message: `User ${
              currentUser.value?.username || currentUser.value?.mobileNumber
            } requested customization for trip: ${
              currentCustomizingTrip.value.place
            } (${currentCustomizingTrip.value.days} days, ₹${
              currentCustomizingTrip.value.price
            }) with preferences: Place: ${customizationForm.value.preferredPlace.trim()}, Members: ${
              customizationForm.value.numberOfMembers
            }, Start Date: ${customizationForm.value.startDate}, End Date: ${customizationForm.value.endDate}${
              customizationForm.value.numberOfAdults
                ? `, Adults: ${customizationForm.value.numberOfAdults}`
                : ""
            }${
              customizationForm.value.numberOfKids
                ? `, Kids: ${customizationForm.value.numberOfKids}`
                : ""
            }${
              customizationForm.value.tripType
                ? `, Trip Type: ${
                    {
                      'family-trip': 'Family Trip',
                      'friends-trip': 'Friends Trip',
                      'womens-trip': "Women's Trip",
                      'honeymoon-trip': 'Honeymoon Trip',
                      'adventure-trip': 'Adventure Trip',
                      'corporate': 'Corporate'
                    }[customizationForm.value.tripType] || customizationForm.value.tripType
                  }`
                : ""
            }${
              customizationForm.value.additionalNotes
                ? ` Additional Notes: ${customizationForm.value.additionalNotes}`
                : ""
            }`,
          };
        } else {
          // General customization request
          requestData = {
            type: "general_customization",
            user: {
              username:
                currentUser.value?.username || currentUser.value?.mobileNumber,
              mobileNumber: currentUser.value?.mobileNumber,
              countryCode: currentUser.value?.countryCode,
              email: currentUser.value?.email,
            },
            customizationData: customizationData,
            message: message,
          };
        }

        // Send API request
        const response = await axios.post("/customize-trip", requestData);

        // Close form
        closeCustomizationForm();

        // Show success message
        Swal.fire({
          title: "Request Sent!",
          text: "Our Travel Expert will reach you soon.",
          icon: "success",
          confirmButtonColor: "#2563eb",
          confirmButtonText: "Great!",
        });
      } catch (error) {
        console.error("Error sending customization request:", error);

        // Show error message
        Swal.fire({
          title: "Error",
          text: "Failed to send customization request. Please try again.",
          icon: "error",
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    const openMedia = (trip) => {
      const currentIndex = currentMediaIndex.value[trip.id] || 0;
      const currentMediaUrl = getCurrentMediaUrl(trip);

      if (currentMediaUrl) {
        // Open media in new tab/window
        window.open(currentMediaUrl, "_blank");
      }
    };

    const scrollToTrips = () => {
      if (tripsSection.value) {
        tripsSection.value.scrollIntoView({ behavior: "smooth" });
      }
    };

    const scrollToCustomization = () => {
      const customizationSection = document.getElementById(
        "customization-section"
      );
      if (customizationSection) {
        customizationSection.scrollIntoView({ behavior: "smooth" });
      }
    };

    const scrollToReviews = () => {
      // Ensure reviews are loaded first
      if (reviewsLoading.value) {
        // Wait for reviews to load, then scroll
        const checkLoading = setInterval(() => {
          if (!reviewsLoading.value) {
            clearInterval(checkLoading);
            scrollToReviews();
          }
        }, 100);
        return;
      }

      // Try multiple approaches to ensure scrolling works
      if (reviewsSection.value) {
        // Method 1: scrollIntoView
        reviewsSection.value.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });

        // Method 2: Alternative scroll approach with slight delay
        setTimeout(() => {
          if (reviewsSection.value) {
            const elementTop = reviewsSection.value.offsetTop;
            const offset = 100; // Add some offset from top
            window.scrollTo({
              top: elementTop - offset,
              behavior: "smooth",
            });
          }
        }, 100);
      } else {
        // Fallback: try to find the element by class
        const reviewsElement = document.querySelector(".reviews-section");
        if (reviewsElement) {
          reviewsElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }
    };

    // Redirect to Google Reviews
    const redirectToGoogleReviews = () => {
      console.log("Review card clicked - redirecting to Google Reviews");
      window.open("https://share.google/zs9jPwySrMxskqxUg", "_blank");
    };

    // Auto-popup timer functions
    const startAutoPopupTimer = () => {
      // Only start timer if we haven't shown the popup yet
      if (hasShownAutoPopup.value) {
        return;
      }

      // Clear any existing timer
      if (autoPopupTimer.value) {
        clearTimeout(autoPopupTimer.value);
      }

      // Start new timer for 30 seconds
      autoPopupTimer.value = setTimeout(() => {
        // Only open if user is authenticated, form is not already open, and we haven't shown it yet
        if (isAuthenticated.value && !showCustomizationForm.value && !hasShownAutoPopup.value) {
          customizeTrip();
          hasShownAutoPopup.value = true; // Mark as shown
        }
      }, 30000); // 30 seconds
    };

    const clearAutoPopupTimer = () => {
      if (autoPopupTimer.value) {
        clearTimeout(autoPopupTimer.value);
        autoPopupTimer.value = null;
      }
      hasShownAutoPopup.value = false; // Reset the flag when clearing timer
    };

    // Review media helper functions
    const isImage = (url) => {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    };

    const isVideo = (url) => {
      return /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(url);
    };

    const openMediaInNewTab = (mediaUrl) => {
      if (isVideo(mediaUrl)) {
        // For videos, create a new window with a video player
        const videoWindow = window.open(
          "",
          "_blank",
          "width=800,height=600,scrollbars=yes,resizable=yes"
        );
        videoWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Video Player</title>
            <style>
              body {
                margin: 0;
                padding: 20px;
                background: #000;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                font-family: Arial, sans-serif;
              }
              video {
                max-width: 100%;
                max-height: 100%;
                border-radius: 8px;
              }
              .close-btn {
                position: absolute;
                top: 10px;
                right: 10px;
                background: rgba(255, 255, 255, 0.2);
                border: none;
                color: white;
                padding: 10px 15px;
                border-radius: 5px;
                cursor: pointer;
                font-size: 16px;
              }
              .close-btn:hover {
                background: rgba(255, 255, 255, 0.3);
              }
            </style>
          </head>
          <body>
            <button class="close-btn" onclick="window.close()">✕ Close</button>
            <video controls autoplay>
              <source src="${mediaUrl}" type="video/mp4">
              <source src="${mediaUrl}" type="video/webm">
              <source src="${mediaUrl}" type="video/ogg">
              Your browser does not support the video tag.
            </video>
          </body>
          </html>
        `);
        videoWindow.document.close();
      } else {
        // For images, open directly
        window.open(mediaUrl, "_blank");
      }
    };

    const viewReviewMedia = (media, index) => {
      // For now, open the media in a new tab
      // You can enhance this to show a modal viewer later
      if (media && media[index]) {
        window.open(media[index], "_blank");
      }
    };

    // Auto-scroll reviews functionality
    const startReviewAutoScroll = () => {
      if (!reviewsScrollContainer.value || reviews.value.length <= 1) return;

      // Clear existing timer
      if (reviewScrollTimer.value) {
        clearInterval(reviewScrollTimer.value);
      }

      // Start auto-scroll every 3 seconds
      reviewScrollTimer.value = setInterval(() => {
        // Don't scroll if user is focused on a review
        if (isReviewFocused.value || !reviewsScrollContainer.value) return;

        const container = reviewsScrollContainer.value;
        const scrollAmount = 280; // Width of one review card + gap
        const maxScroll = container.scrollWidth - container.clientWidth;
        const currentScroll = container.scrollLeft;

        if (currentScroll >= maxScroll) {
          // Reset to beginning
          container.scrollTo({
            left: 0,
            behavior: "smooth",
          });
        } else {
          // Scroll to next review
          container.scrollTo({
            left: currentScroll + scrollAmount,
            behavior: "smooth",
          });
        }
      }, 3000);
    };

    const stopReviewAutoScroll = () => {
      if (reviewScrollTimer.value) {
        clearInterval(reviewScrollTimer.value);
        reviewScrollTimer.value = null;
      }
    };

    // Pause auto-scroll when user interacts with reviews
    const pauseReviewAutoScroll = () => {
      isReviewFocused.value = true;
    };

    // Resume auto-scroll when user stops interacting
    const resumeReviewAutoScroll = () => {
      isReviewFocused.value = false;
    };

    // Computed property for displaying trips (filtered by screen size)
    const displayedTrips = computed(() => {
      const totalTrips = trips.value.length;

      // If showing all trips, return all
      if (showAllTripsMobile.value) {
        return trips.value;
      }

      // Mobile: show 3 trips
      if (isMobile.value) {
        return trips.value.slice(0, 3);
      }

      // Tablet/Desktop: show 6 trips
      return trips.value.slice(0, 6);
    });

    // Check if there are more trips to show
    const hasMoreTrips = computed(() => {
      const totalTrips = trips.value.length;
      if (isMobile.value) {
        return totalTrips > 3;
      }
      return totalTrips > 6;
    });

    // Get the number of hidden trips
    const hiddenTripsCount = computed(() => {
      const totalTrips = trips.value.length;
      if (isMobile.value) {
        return totalTrips - 3;
      }
      return totalTrips - 6;
    });

    // Toggle show all trips
    const toggleShowAllTrips = () => {
      showAllTripsMobile.value = !showAllTripsMobile.value;
    };

    // Scroll to philosophy section
    const scrollToPhilosophy = () => {
      const philosophySection = document.querySelector(".philosophy-section");
      if (philosophySection) {
        philosophySection.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    };

    // Logout function
    const logout = async () => {
      try {
        await store.dispatch("auth/logout");
        router.push("/trips");
      } catch (error) {
        console.error("Logout error:", error);
      }
    };

    // Toggle user dropdown
    const toggleUserDropdown = () => {
      showUserDropdown.value = !showUserDropdown.value;
    };

    // Global click handler for unauthenticated users
    const handleGlobalClick = (event) => {
      // Check if user is not authenticated
      if (!isAuthenticated.value) {
        const target = event.target;
        const isPublicFooterAction =
          target.closest(".footer-legal-btn") ||
          target.closest(".footer-legal-links") ||
          target.closest(".legal-modal-overlay") ||
          target.closest("footer a[href^='mailto:']") ||
          target.closest("footer a[href^='https://']") ||
          target.closest("footer a[href^='tel:']");

        if (isPublicFooterAction) {
          return;
        }

        // Prevent the default action
        event.preventDefault();
        event.stopPropagation();

        // Direct redirect to login page
        router.push("/login");
      }
    };

    // Watch for authentication state changes
    watch(isAuthenticated, (newValue) => {
      if (newValue) {
        // User logged in, remove global click handler
        document.removeEventListener("click", handleGlobalClick, true);
      } else {
        // User logged out, add global click handler
        document.addEventListener("click", handleGlobalClick, true);
      }
    });

    // Lifecycle hooks
    onMounted(async () => {
      // Set screen size state immediately
      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);

      await loadStatistics(); // Load admin-configured statistics
      await loadTrips();
      await loadReviews();

      // Start auto-scroll for reviews after DOM is updated
      setTimeout(() => {
        startReviewAutoScroll();
      }, 100);

      // Start auto-popup timer for customization form
      startAutoPopupTimer();

      // Add global click listener for unauthenticated users
      if (!isAuthenticated.value) {
        document.addEventListener("click", handleGlobalClick, true);
      }
    });

    onUnmounted(() => {
      window.removeEventListener('resize', checkScreenSize);
      stopAutoAdvance();
      stopReviewAutoScroll();
      clearAutoPopupTimer();
      // Remove global click listener
      document.removeEventListener("click", handleGlobalClick, true);
    });

    return {
      trips,
      reviews,
      loading,
      reviewsLoading,
      error,
      reviewsError,
      statistics,
      loadStatistics,
      saveStatistics,
      currentMediaIndex,
      tripsSection,
      currentUser,
      isAuthenticated,
      userType,
      showCustomizationForm,
      isSubmitting,
      currentCustomizingTrip,
      customizationForm,
      loadTrips,
      nextMedia,
      previousMedia,
      goToMedia,
      isCurrentMediaImage,
      isCurrentMediaVideo,
      getCurrentMediaUrl,
      bookNow,
      viewItinerary,
      customizeTrip,
      closeCustomizationForm,
      submitCustomizationForm,
      openMedia,
      scrollToTrips,
      scrollToCustomization,
      scrollToReviews,
      redirectToGoogleReviews,
      isImage,
      isVideo,
      viewReviewMedia,
      openMediaInNewTab,
      reviewsScrollContainer,
      reviewsScrollRow,
      startReviewAutoScroll,
      stopReviewAutoScroll,
      pauseReviewAutoScroll,
      resumeReviewAutoScroll,
      displayedTrips,
      hasMoreTrips,
      hiddenTripsCount,
      showAllTripsMobile,
      toggleShowAllTrips,
      formatDate,
      getTripDateDisplay,
      getGroupSizeDisplay,
      startAutoPopupTimer,
      clearAutoPopupTimer,
      hasShownAutoPopup,
      scrollToPhilosophy,
      logout,
      showUserDropdown,
      toggleUserDropdown,
    };
  },
};
</script>

<style scoped>
/* CSS Variables */
:root {
  --primary-color: #2563eb;
  --secondary-color: #059669;
  --accent-color: #ef4444;
  --dark-color: #1e293b;
  --light-color: #f8fafc;
  --success-color: #10b981;
  --warning-color: #f59e0b;
  --danger-color: #ef4444;
}

/* Base Styles */
.public-trips {
  min-height: 100vh;
  overflow-y: auto;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Reset for hero section */
.public-trips {
  margin: 0;
  padding: 0;
}

/* Hero Section */
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-image: url("https://gypsy-souls-website.s3.ap-south-1.amazonaws.com/login-bg.jpg");
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-origin: border-box;
  background-clip: border-box;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: transform;
  overflow: hidden;
  padding: 0;
  margin: 0;
  z-index: 1;
}

/* Hero Header */
.hero-header {
  position: relative;
  z-index: 10;
  background: transparent;
  padding: 0;
  margin: 0 0 2rem 0;
  width: 100%;
}

.hero-navbar-content {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 60px;
  padding: 0 2rem;
}

.hero-left-section {
  display: flex !important;
  align-items: center;
  gap: 2rem;
  flex: 0 0 auto;
  margin-left: 0;
}

.hero-right-section {
  display: flex !important;
  align-items: center;
  gap: 2rem;
  flex: 0 0 auto;
  margin-right: 0;
}

.hero-brand {
  font-size: 1.5rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  color: white;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.hero-logo {
  height: 80px;
  width: auto;
  margin-right: 0.5rem;
  border-radius: 50%;
  margin-top :0.5rem;
}

.hero-nav-menu {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.hero-nav-list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 1rem;
}

.hero-nav-item {
  margin: 0;
}

.hero-nav-link {
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.hero-nav-link:hover {
  color: #ff8c42;
}

.hero-phone {
  display: flex;
  align-items: center;
}

.hero-phone-link {
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.hero-phone-link:hover {
  color: #b46428;
  text-decoration: none;
}

.hero-user-menu {
  display: flex;
  align-items: center;
}

.hero-auth-links {
  display: flex;
  gap: 1rem;
}

.hero-auth-link {
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.hero-auth-link:hover {
  color: #ff8c42;
}

.hero-user-dropdown {
  position: relative;
}

.hero-user-btn {
  background: none;
  border: none;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.hero-user-btn:hover {
  color: #ff8c42;
  background: rgba(255, 140, 66, 0.2);
}

.hero-user-btn.active {
  background: rgba(255, 140, 66, 0.2);
}

.hero-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: rgba(44, 36, 25, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 0;
  min-width: 150px;
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.hero-dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
}

.hero-dropdown-item:hover {
  background: rgba(255, 140, 66, 0.2);
  color: #ff8c42;
}

.hero-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(44, 36, 25, 0.6) 0%,
    rgba(180, 100, 40, 0.4) 30%,
    rgba(120, 80, 30, 0.5) 60%,
    rgba(50, 55, 60, 0.7) 100%
  );
  z-index: 1;
}

.hero-background-animation {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  animation: float 20s ease-in-out infinite;
  z-index: 2;
}

.hero-content {
  text-align: center;
  color: white;
  z-index: 3;
  position: relative;
  max-width: 800px;
  padding: 0 2rem;
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;
}

.hero-main-text {
  display: block;
  margin-bottom: 0.5rem;
}

.hero-accent-text {
  display: block;
  background: linear-gradient(135deg, #ff8c42, #b46428, #96323c);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-second-tagline {
  margin: 1.5rem 0;
  text-align: center;
}

.second-tagline-text {
  font-size: 1.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.5px;
  font-style: italic;
}

.hero-subtitle {
  font-size: 1.5rem;
  margin-bottom: 3rem;
  opacity: 0.9;
  line-height: 1.6;
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
}

.stat-item {
  text-align: center;
  padding: 1.5rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  color: white;
}

.stat-label {
  font-size: 1rem;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Hero Action Buttons */
/* Hero Rating Icons */
.hero-ratings {
  display: flex;
  gap: 2rem;
  justify-content: center;
  margin: 2rem 0;
  flex-wrap: wrap;
  cursor: pointer;
  border-radius: 20px;
  padding: 1rem;
}

.rating-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  width: 120px;
  height: 120px;
  padding: 1rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.rating-icon:hover {
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.3);
}

.rating-platforms {
  display: flex;
  gap: 0.8rem;
  margin-bottom: 0.5rem;
  align-items: center;
}

.platform-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.google-icon svg {
  width: 28px;
  height: 28px;
}

.facebook-icon i {
  font-size: 1.2rem;
  color: #1877f2;
}

.rating-stars {
  display: flex;
  gap: 0.2rem;
  margin-bottom: 0.5rem;
}

.rating-stars i {
  color: #ffd700;
  font-size: 0.8rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.rating-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  display: contents;
}

.hero-actions {
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin: 2rem 0;
  flex-wrap: wrap;
}

.hero-btn {
  border: none;
  border-radius: 50px;
  padding: 1.2rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.hero-btn.primary {
  background: linear-gradient(135deg, #b46428, #96323c);
}

.hero-btn.customize {
  background: linear-gradient(135deg, #ff8c42, #b46428);
  animation: pulse-glow 2s infinite;
}

.hero-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.5s ease;
}

.hero-btn:hover::before {
  left: 100%;
}

.hero-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.4);
}

.hero-btn i {
  margin-left: 0.5rem;
  transition: transform 0.3s ease;
}

.hero-btn:hover i {
  transform: translateX(5px);
}

.hero-btn.customize:hover {
  background: linear-gradient(135deg, #d4a574, #ff8c42);
}

/* Customization Highlight */
.customization-highlight {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 1.5rem 2rem;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(37, 99, 235, 0.2);
  animation: slideInUp 0.8s ease-out;
}

.highlight-icon {
  background: linear-gradient(135deg, #2563eb, #059669);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
  animation: rotate 3s linear infinite;
}

.highlight-content h3 {
  color: var(--dark-color);
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-align: center;
}

.highlight-content p {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
  text-align: center;
}

/* Animations */
@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.3);
  }
  50% {
    box-shadow: 0 8px 35px rgba(37, 99, 235, 0.5);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Trips Section */
.trips-section {
  padding: 6rem 0;
  background: linear-gradient(135deg, #f5f2ed 0%, #e8e0d6 50%, #d4c4b0 100%);
  position: relative;
}

.trips-section .section-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("https://gypsy-souls-website.s3.ap-south-1.amazonaws.com/login-bg.jpg");
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  opacity: 0.15;
  z-index: 0;
}

.trips-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 3px;
  background: linear-gradient(
    90deg,
    var(--primary-color),
    var(--secondary-color)
  );
  border-radius: 2px;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-icon-wrapper {
  display: inline-block;
  margin-bottom: 1.5rem;
}

.section-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #b46428, #78501e);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 8px 25px rgba(44, 36, 25, 0.2);
  animation: pulse 2s infinite;
}

.section-icon i {
  font-size: 2rem;
  color: white;
}

.section-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.title-main {
  display: block;
  color: white;
}

.title-accent {
  display: block;
  background: linear-gradient(135deg, #b46428, #78501e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 600;
}

.section-subtitle {
  font-size: 1.3rem;
  color: #6c757d;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.section-decoration {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.decoration-line {
  width: 60px;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--primary-color),
    transparent
  );
}

.decoration-dot {
  width: 8px;
  height: 8px;
  background: var(--secondary-color);
  border-radius: 50%;
  animation: bounce 2s infinite;
}

/* Trips Grid */
.trips-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;
  justify-items: center;
}

/* Show More Button Container */
.show-more-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  margin-bottom: 1rem;
  z-index: 10;
  position: relative;
  clear: both;
}

.show-more-btn {
  background: linear-gradient(135deg, #5a6446 0%, #78501e 100%);
  border: none;
  border-radius: 12px;
  padding: 0.75rem 2rem;
  font-weight: 600;
  font-size: 1rem;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(90, 100, 70, 0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  pointer-events: auto;
  position: relative;
  z-index: 10;
  white-space: nowrap;
  width: auto;
  min-width: auto;
  max-width: none;
}

.show-more-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(90, 100, 70, 0.4);
}

.show-more-btn:active {
  transform: translateY(0);
}

.show-more-btn i {
  font-size: 0.9rem;
}

.trip-card {
  background: linear-gradient(135deg, #f5f2ed 0%, #e8e0d6 100%);
  border-radius: 30px;
  overflow: visible;
  box-shadow: 0 12px 35px rgba(44, 36, 25, 0.15),
    0 4px 15px rgba(44, 36, 25, 0.1);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  min-height: 380px;
  width: 100%;
  min-width: 320px;
  max-width: 100%;
  pointer-events: auto;
  border: 1px solid rgba(180, 100, 40, 0.3);
  backdrop-filter: blur(10px);
  padding: 8px;
}

.trip-card::after {
  content: "";
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  bottom: 8px;
  background: linear-gradient(135deg, #f5f2ed 0%, #e8e0d6 100%);
  border-radius: 22px;
  box-shadow: inset 0 2px 10px rgba(44, 36, 25, 0.08),
    0 1px 3px rgba(44, 36, 25, 0.1);
  border: 1px solid rgba(180, 100, 40, 0.2);
  z-index: 1;
}

.trip-card::before {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  right: 4px;
  bottom: 4px;
  background: linear-gradient(
    135deg,
    rgba(180, 100, 40, 0.2),
    rgba(120, 80, 30, 0.15),
    rgba(180, 100, 40, 0.1)
  );
  border-radius: 26px;
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: 2;
}

.trip-card:hover::before {
  opacity: 1;
}

.trip-card:hover {
  transform: translateY(-8px) scale(1.01);
  box-shadow: 0 20px 50px rgba(44, 36, 25, 0.2),
    0 6px 20px rgba(180, 100, 40, 0.15);
  border-color: rgba(180, 100, 40, 0.4);
}

/* Carousel Styles */
.carousel-container {
  position: relative;
  height: 200px;
  overflow: hidden;
  cursor: pointer;
  z-index: 3;
  pointer-events: auto;
  border-radius: 22px 22px 0 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: none;
}

.carousel-content {
  width: 100%;
  height: 100%;
  position: relative;
}

.carousel-image,
.carousel-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  display: block;
  cursor: pointer;
}

.carousel-video {
  background: #000;
}

.carousel-image:hover,
.carousel-video:hover {
  transform: scale(1.05);
}

.no-media-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  color: #6c757d;
  font-size: 0.9rem;
}

.no-media-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
}

.carousel-nav.prev {
  left: 10px;
}

.carousel-nav.next {
  right: 10px;
}

.carousel-nav:hover {
  background: white;
  transform: translateY(-50%) scale(1.1);
}

.carousel-nav i {
  color: var(--primary-color);
  font-size: 0.9rem;
}

.thumbnails-container {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 2;
}

.thumbnail-item {
  width: 50px;
  height: 30px;
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-item.active {
  border-color: var(--secondary-color);
  transform: scale(1.1);
}

/* Card Body */
.card-body {
  padding: 1rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 3;
  pointer-events: auto;
  border-radius: 0 0 22px 22px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-top: none;
}

.trip-title {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  position: relative;
  flex-wrap: wrap;
}

.trip-itinerary-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.6rem;
  width: 100%;
}

.trip-title::after {
  content: "";
  position: absolute;
  bottom: -0.5rem;
  left: 0;
  width: 40px;
  height: 2px;
  background: linear-gradient(
    90deg,
    var(--primary-color),
    var(--secondary-color)
  );
  border-radius: 1px;
}

.location-icon {
  width: 24px;
  height: 24px;
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--secondary-color)
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.8rem;
}

.location-icon i {
  color: white;
  font-size: 0.8rem;
}

.location-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--dark-color);
  margin: 0;
  position: relative;
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.3;
}

.location-name::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--secondary-color)
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* Compact View Itinerary Button */
.view-itinerary-btn-compact {
  background: linear-gradient(135deg, #5a6446 0%, #78501e 100%);
  border: none;
  border-radius: 10px;
  padding: 0.2rem 0.4rem;
  font-weight: 600;
  font-size: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  color: white;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  position: relative;
  z-index: 100;
  user-select: none;
  pointer-events: auto;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(90, 100, 70, 0.3);
  min-height: 24px;
  max-height: 24px;
  flex-shrink: 0;
  /* Ensure button is clickable */
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
}

.view-itinerary-btn-compact::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.6s ease;
}

.view-itinerary-btn-compact:hover {
  background: linear-gradient(135deg, #8b7355 0%, #5a6446 100%);
  transform: translateY(-1px) scale(1.03);
  box-shadow: 0 6px 20px rgba(90, 100, 70, 0.4);
}

.view-itinerary-btn-compact:hover::before {
  left: 100%;
}

.view-itinerary-btn-compact:active {
  transform: translateY(-1px) scale(1.02);
}

.view-itinerary-btn-compact i {
  font-size: 0.5rem;
  transition: transform 0.3s ease;
}

.view-itinerary-btn-compact:hover i {
  transform: rotate(5deg);
}

.trip-card:hover .location-name::before {
  opacity: 1;
}

/* Trip Pricing Row */
.trip-pricing-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
  padding: 0.6rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(248, 249, 250, 0.9) 100%
  );
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.trip-duration-group-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
  padding: 0.6rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(248, 249, 250, 0.9) 100%
  );
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.trip-dates-row {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.6rem;
  padding: 0.6rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(248, 249, 250, 0.9) 100%
  );
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.group-size-display {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--dark-color);
  font-weight: 600;
  padding: 0.4rem 0.7rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.group-size-display::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(44, 90, 160, 0.05),
    rgba(248, 183, 57, 0.05)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}

.trip-card:hover .group-size-display::before {
  opacity: 1;
}

.group-size-text {
  color: var(--primary-color);
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.price-display {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.7rem;
  color: var(--dark-color);
  font-weight: 600;
}

.starts-from {
  color: #6c757d;
  font-size: 0.65rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.price-amount {
  display: flex;
  align-items: baseline;
  gap: 0.1rem;
}

.currency {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 0.8rem;
}

.amount {
  color: var(--primary-color);
  font-weight: 800;
  font-size: 1.1rem;
}

.per-person {
  color: #6c757d;
  font-size: 0.6rem;
  font-weight: 500;
  margin-left: 0.15rem;
  opacity: 0.8;
}

.duration-display {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--dark-color);
  font-weight: 600;
  padding: 0.4rem 0.7rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.duration-display::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(44, 90, 160, 0.05),
    rgba(248, 183, 57, 0.05)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}

.trip-card:hover .duration-display::before {
  opacity: 1;
}

.duration-text {
  color: var(--primary-color);
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.trip-meta {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.6rem;
  padding: 0.5rem;
  background: linear-gradient(
    135deg,
    rgba(248, 249, 250, 0.8) 0%,
    rgba(233, 236, 239, 0.8) 100%
  );
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.meta-item {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #6c757d;
  padding: 0.4rem 0.6rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 10px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  gap: 0.4rem;
  font-weight: 500;
}

.meta-separator {
  margin: 0 0.3rem;
  color: #adb5bd;
  font-weight: 600;
  font-size: 0.8rem;
}

.group-size-text {
  margin-left: 0.2rem;
  color: #495057;
  font-weight: 500;
}

.meta-item:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateX(5px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.meta-item i {
  width: 16px;
  margin-right: 0.2rem;
  font-size: 0.75rem;
  color: var(--primary-color);
  opacity: 0.8;
}

.trip-description {
  margin-bottom: 0.6rem;
  padding: 0.5rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(248, 249, 250, 0.9) 100%
  );
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;
}

.trip-description::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(
    180deg,
    var(--primary-color),
    var(--secondary-color)
  );
  border-radius: 2px;
}

.description-text {
  line-height: 1.3;
  color: var(--dark-color);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  max-height: 3.9em;
  padding-left: 0.3rem;
  font-weight: 500;
  font-size: 0.75rem;
}

/* OLD CSS - COMMENTED OUT TO AVOID CONFLICTS
.action-buttons {
  display: flex;
  gap: 0.6rem;
  margin-top: 0.8rem;
  padding: 0.4rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(248, 249, 250, 0.9) 100%
  );
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  flex-wrap: wrap;
  position: relative;
  z-index: 5;
  pointer-events: auto;
}
*/

/* OLD BUTTON CSS - COMMENTED OUT TO AVOID CONFLICTS
.book-now-btn,
.customize-trip-btn {
  min-width: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 28px;
  max-height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  word-break: keep-all;
  line-height: 1.1;
  font-size: 0.5rem;
  padding: 0.4rem 0.8rem;
  cursor: pointer;
  user-select: none;
  position: relative;
  z-index: 10;
  pointer-events: auto;
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
}

.book-now-btn:hover,
.customize-trip-btn:hover {
  border-color: #ff0000;
  transform: scale(1.05);
}
*/

.book-now-btn i,
.view-itinerary-btn i,
.customize-trip-btn i {
  margin-right: 0.3rem;
  flex-shrink: 0;
  font-size: 0.6rem;
}

.book-now-btn {
  background: linear-gradient(135deg, #2563eb, #059669);
  border: none;
  border-radius: 12px;
  padding: 0.3rem 0.6rem;
  font-weight: 600;
  font-size: 0.45rem;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-height: 28px;
  max-height: 28px;
}

.book-now-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}

.book-now-btn:hover::before {
  left: 100%;
}

.book-now-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(0, 0, 0, 0.2);
  background: linear-gradient(135deg, #1d4ed8, #047857);
}

.view-itinerary-btn {
  background: linear-gradient(135deg, #b46428 0%, #96323c 100%);
  border: none;
  border-radius: 18px;
  padding: 0.35rem 0.8rem;
  font-weight: 600;
  font-size: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: white;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(180, 100, 40, 0.3);
  min-height: 28px;
  max-height: 28px;
}

.view-itinerary-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transition: left 0.6s ease;
}

.view-itinerary-btn::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.6s ease;
}

.view-itinerary-btn:hover {
  background: linear-gradient(135deg, #d4a574 0%, #b46428 100%);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 25px rgba(180, 100, 40, 0.4);
}

.view-itinerary-btn:hover::before {
  left: 100%;
}

.view-itinerary-btn:hover::after {
  width: 300px;
  height: 300px;
}

.view-itinerary-btn:active {
  transform: translateY(-1px) scale(1.05);
}

.view-itinerary-btn i {
  transition: transform 0.3s ease;
}

.view-itinerary-btn:hover i {
  transform: rotate(10deg) scale(1.1);
}

.customize-trip-btn {
  background: linear-gradient(135deg, #b46428, #96323c);
  border: none;
  border-radius: 12px;
  padding: 0.3rem 0.6rem;
  font-weight: 600;
  font-size: 0.45rem;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  color: white;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(180, 100, 40, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 28px;
  max-height: 28px;
}

.customize-trip-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}

.customize-trip-btn:hover::before {
  left: 100%;
}

.customize-trip-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(180, 100, 40, 0.3), 0 4px 8px rgba(0, 0, 0, 0.2);
  background: linear-gradient(135deg, #d4a574, #b46428);
}

/* Reviews Section */
.reviews-section {
  padding: 6rem 0;
  background: linear-gradient(135deg, #e8e0d6 0%, #d4c4b0 50%, #c4b5a0 100%);
  position: relative;
}

.reviews-section .section-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("@/assets/1.jpg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.1;
}

.reviews-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.review-card {
  border: none;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: white;
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.review-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    var(--primary-color),
    var(--secondary-color)
  );
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.review-card:hover::before {
  transform: scaleX(1);
}

.review-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  border: 2px solid var(--primary-color);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.reviewer-name {
  font-weight: 600;
  color: var(--dark-color);
  margin-bottom: 0.5rem;
}

.rating i {
  font-size: 0.9rem;
  margin-right: 0.1rem;
  color: var(--secondary-color);
}

.review-text {
  color: #6c757d;
  line-height: 1.6;
  font-style: italic;
  margin-bottom: 1rem;
  /* Ensure emojis render correctly */
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "Android Emoji", "EmojiSymbols", "EmojiOne Mozilla", "Twemoji Mozilla", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  font-feature-settings: "liga" off;
  -webkit-font-feature-settings: "liga" off;
  -moz-font-feature-settings: "liga" off;
  text-rendering: optimizeLegibility;
}

.review-trip {
  border-top: 1px solid #e9ecef;
  padding-top: 0.75rem;
  font-size: 0.9rem;
  color: var(--primary-color);
}

/* Customization Section */
.customization-section {
  padding: 6rem 0;
  background: url("@/assets/1.jpg") center center / cover no-repeat;
  position: relative;
  overflow: hidden;
}

.customization-section::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 0;
}

.customization-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  animation: float 20s ease-in-out infinite;
  z-index: 1;
}

.customization-content {
  text-align: center;
  color: white;
  position: relative;
  z-index: 3;
}

.customization-icon-wrapper {
  display: inline-block;
  margin-bottom: 2rem;
}

.customization-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #059669, #ef4444);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  animation: pulse 2s infinite, magicGlow 3s ease-in-out infinite;
}

.customization-icon i {
  font-size: 2.5rem;
  color: white;
}

.customization-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;
}

.customization-title .title-main {
  display: block;
  margin-bottom: 0.5rem;
}

.customization-title .title-accent {
  display: block;
  background: linear-gradient(135deg, #b46428, #78501e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 600;
}

.customization-subtitle {
  font-size: 1.3rem;
  margin-bottom: 3rem;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

.customization-features {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
}

.feature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  min-width: 150px;
}

.feature-item:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.2);
}

.feature-item i {
  font-size: 2rem;
  color: var(--secondary-color);
}

.feature-item span {
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
}

.customization-cta {
  margin-top: 2rem;
}

.customization-btn {
  background: linear-gradient(135deg, #059669, #ef4444, #2563eb, #10b981);
  background-size: 300% 300%;
  border: none;
  border-radius: 50px;
  padding: 1.2rem 3rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  animation: gradientShift 3s ease infinite;
}

.customization-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.5s ease;
}

.customization-btn:hover::before {
  left: 100%;
}

.customization-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.4);
}

/* Loading and Error States */
.loading-container,
.error-container,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 2rem;
}

.error-icon,
.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.retry-btn {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: var(--secondary-color);
  transform: translateY(-2px);
}

/* Animations */
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes magicGlow {
  0%,
  100% {
    box-shadow: 0 0 20px rgba(248, 183, 57, 0.5);
  }
  50% {
    box-shadow: 0 0 40px rgba(248, 183, 57, 0.8);
  }
}

/* Responsive Design */
/* Customization Modal Styles */
.customization-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
}

.customization-modal {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 700;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 0 24px 24px 24px;
}

.customization-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-row .form-group {
  flex: 1;
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }
}

.form-group label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.required {
  color: #dc2626;
  font-weight: bold;
}

.form-control {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: #fafafa;
}

.form-control:focus {
  outline: none;
  border-color: #2563eb;
  background: white;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-control::placeholder {
  color: #9ca3af;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
}

.form-actions .btn {
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb 0%, #059669 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #1d4ed8 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 1400px) {
  .trips-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.2rem;
  }

  .trip-card {
    min-width: 300px;
  }
}

@media (max-width: 992px) {
  .trips-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .trip-card {
    min-width: 280px;
  }

  .customization-modal {
    width: 95%;
    margin: 20px;
  }

  .modal-header {
    padding: 20px 20px 0 20px;
  }

  .modal-body {
    padding: 0 20px 20px 20px;
  }

  .form-actions {
    flex-direction: column;
  }

  .form-actions .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .trips-grid {
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem;
    justify-items: center;
  }

  .trip-card {
    min-width: 85%;
    max-width: 90%;
  }

  .carousel-container {
    height: 250px;
  }

  .show-more-btn {
    padding: 0.6rem 1.5rem;
    font-size: 0.9rem;
  }

  .hero-section {
    padding-top: 80px;
    min-height: 100vh;
    justify-content: center;
  }

  .hero-navbar-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 1.5rem;
    min-height: 70px;
  }

  .hero-left-section {
    flex-direction: row;
    gap: 1rem;
    flex: 0 0 auto;
    margin-left: 0;
  }

  .hero-right-section {
    flex-direction: row;
    gap: 1rem;
    flex: 0 0 auto;
    margin-right: 0;
  }

  .hero-nav-menu {
    display: none; /* Hide nav menu on mobile */
  }

  .hero-auth-links {
    justify-content: flex-end;
    gap: 0.8rem;
  }

  .hero-phone {
    justify-content: flex-end;
  }

  .hero-brand {
    font-size: 1.3rem;
  }

  .hero-logo {
    height: 75px;
  }

  .hero-content {
    padding: 2rem 1.5rem;
    max-width: 100%;
  }

  .hero-title {
    font-size: 2.8rem;
    line-height: 1.1;
    margin-bottom: 1rem;
  }

  .second-tagline-text {
    font-size: 1.4rem;
    margin: 1rem 0;
  }

  .hero-subtitle {
    font-size: 1.2rem;
    margin-bottom: 2.5rem;
    line-height: 1.5;
  }

  .hero-stats {
    gap: 1rem;
    margin-bottom: 2.5rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .stat-item {
    padding: 1.2rem 1.5rem;
    min-width: 140px;
    flex: 1;
    max-width: 160px;
  }

  .stat-number {
    font-size: 2.2rem;
  }

  .stat-label {
    font-size: 0.85rem;
  }

  .hero-ratings {
    gap: 1.2rem;
    margin-bottom: 2.5rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .rating-icon {
    width: 90px;
    height: 90px;
    padding: 0.7rem;
  }

  .rating-platforms {
    gap: 0.5rem;
  }

  .google-icon svg {
    width: 22px;
    height: 22px;
  }

  .facebook-icon i {
    font-size: 1rem;
  }

  .rating-stars i {
    font-size: 0.65rem;
  }

  .rating-text {
    font-size: 0.9rem;
  }

  .hero-actions {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }

  .hero-btn {
    padding: 1rem 2rem;
    font-size: 1rem;
    width: 100%;
    max-width: 280px;
    border-radius: 12px;
  }

  .customization-highlight {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem;
  }

  .highlight-content h3 {
    font-size: 1.2rem;
  }

  .section-title {
    font-size: 2.5rem;
  }

  .section-icon {
    width: 60px;
    height: 60px;
  }

  .section-icon i {
    font-size: 1.5rem;
  }

  .customization-section {
    padding: 4rem 0;
  }

  .customization-title {
    font-size: 2.5rem;
  }

  .customization-icon {
    width: 70px;
    height: 70px;
  }

  .customization-icon i {
    font-size: 2rem;
  }

  .customization-subtitle {
    font-size: 1.2rem;
  }

  .customization-features {
    gap: 1rem;
  }

  .feature-item {
    padding: 1rem 1.5rem;
    min-width: 120px;
  }

  .feature-item i {
    font-size: 1.5rem;
  }

  .feature-item span {
    font-size: 0.9rem;
  }

  .customization-btn {
    padding: 1rem 2.5rem;
    font-size: 1.1rem;
  }

  .trips-section,
  .reviews-section {
    padding: 4rem 0;
  }

  .trip-card {
    min-height: 380px;
  }

  .carousel-container {
    height: 250px;
  }

  .thumbnail-item {
    width: 40px;
    height: 40px;
  }

  .trip-title {
    font-size: 1.1rem;
  }

  .location-name {
    font-size: 1.3rem;
    word-wrap: break-word;
    word-break: break-word;
  }

  .price-display {
    font-size: 1.1rem;
  }

  .amount {
    font-size: 1.3rem;
  }

  .duration-display {
    font-size: 0.7rem;
    align-self: flex-start;
  }

  .action-buttons {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
  }

  .book-now-btn,
  .view-itinerary-btn,
  .customize-trip-btn {
    padding: 0.5rem 1rem;
    font-size: 0.6rem;
    min-height: 36px;
    max-height: 36px;
    width: 100%;
    flex: none;
  }

  .description-text {
    -webkit-line-clamp: 4;
    max-height: 5.6em;
  }

  .trip-pricing-row {
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
    padding: 1rem;
    text-align: center;
  }

  .trip-duration-group-row {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0.8rem;
    padding: 1rem;
  }

  .trip-dates-row {
    padding: 1rem;
    text-align: center;
  }

  .price-display {
    font-size: 1.2rem;
    justify-content: center;
  }

  .starts-from {
    font-size: 0.8rem;
  }

  .amount {
    font-size: 1.5rem;
  }

  .per-person {
    font-size: 0.8rem;
  }

  .duration-display {
    font-size: 0.9rem;
    padding: 0.6rem 1rem;
    flex: 1;
    text-align: center;
  }

  .group-size-display {
    font-size: 0.9rem;
    padding: 0.6rem 1rem;
    flex: 1;
    text-align: center;
  }

  .meta-item {
    font-size: 0.9rem;
    justify-content: center;
  }

  .meta-item i {
    font-size: 1rem;
    margin-right: 0.5rem;
  }
}

@media (max-width: 576px) {
  .hero-section {
    padding-top: 70px;
    min-height: 100vh;
  }

  .hero-navbar-content {
    padding: 0 1rem;
    min-height: 60px;
  }

  .hero-brand {
    font-size: 1.1rem;
  }

  .hero-logo {
    height: 70px;
  }

  .hero-auth-links {
    gap: 0.5rem;
  }

  .hero-content {
    padding: 1.5rem 1rem;
    max-width: 100%;
  }

  .hero-title {
    font-size: 2.2rem;
    line-height: 1.1;
    margin-bottom: 0.8rem;
  }

  .second-tagline-text {
    font-size: 1.1rem;
    margin: 0.8rem 0;
  }

  .hero-subtitle {
    font-size: 1rem;
    margin-bottom: 2rem;
    line-height: 1.4;
  }

  .hero-stats {
    gap: 0.8rem;
    margin-bottom: 2rem;
    flex-direction: column;
    align-items: center;
  }

  .stat-item {
    padding: 1rem 1.2rem;
    min-width: 120px;
    max-width: 200px;
    width: 100%;
  }

  .stat-number {
    font-size: 1.8rem;
  }

  .stat-label {
    font-size: 0.8rem;
  }

  .hero-ratings {
    gap: 1rem;
    margin-bottom: 2rem;
    flex-direction: column;
    align-items: center;
  }

  .rating-icon {
    width: 80px;
    height: 80px;
    padding: 0.5rem;
  }

  .rating-platforms {
    gap: 0.4rem;
  }

  .google-icon svg {
    width: 20px;
    height: 20px;
  }

  .facebook-icon i {
    font-size: 0.9rem;
  }

  .rating-stars i {
    font-size: 0.55rem;
  }

  .rating-text {
    font-size: 0.8rem;
  }

  .hero-actions {
    gap: 0.8rem;
    width: 100%;
  }

  .hero-btn {
    padding: 0.9rem 1.5rem;
    font-size: 0.95rem;
    width: 100%;
    max-width: 260px;
    border-radius: 10px;
  }

  .customization-highlight {
    padding: 1.2rem;
    margin-top: 1.5rem;
  }

  .highlight-icon {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }

  .highlight-content h3 {
    font-size: 1.1rem;
  }

  .highlight-content p {
    font-size: 0.9rem;
  }

  .price-display {
    font-size: 1.5rem;
  }

  .amount {
    font-size: 1.5rem;
  }

  .duration-display {
    font-size: 0.8rem;
  }

  .trip-card {
    min-height: 500px;
    margin-bottom: 1.5rem;
    padding: 12px;
    min-width: 85%;
    max-width: 90%;
    margin-left: auto;
    margin-right: auto;
  }

  .carousel-container {
    height: 280px;
  }

  /* Reduce content size for mobile */
  .trip-title {
    padding: 0.8rem 0.6rem;
  }

  .location-name {
    font-size: 1.2rem;
  }

  .trip-pricing-row {
    padding: 0.6rem;
    gap: 0.4rem;
  }

  .trip-duration-group-row {
    padding: 0.6rem;
    gap: 0.4rem;
  }

  .trip-dates-row {
    padding: 0.6rem;
  }

  .trip-description {
    padding: 0.6rem;
  }

  .description-text {
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .price-display {
    font-size: 0.9rem;
  }

  .duration-text, .group-size-text {
    font-size: 0.7rem;
  }

  /* Reduce button sizes for mobile */
  .view-itinerary-btn-compact {
    padding: 0.4rem 0.8rem;
    font-size: 0.7rem;
    min-height: 28px;
    max-height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .book-now-btn,
  .customize-trip-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.7rem;
    min-height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }

  .view-itinerary-btn-compact i,
  .book-now-btn i,
  .customize-trip-btn i {
    font-size: 0.6rem;
  }

  .carousel-container {
    height: 200px;
  }

  .trip-title {
    padding: 1rem 0.8rem;
  }

  .location-name {
    font-size: 1.3rem;
    word-wrap: break-word;
    word-break: break-word;
  }

  .view-itinerary-btn-compact {
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
  }

  /* Enhanced mobile styles for trip cards */
  .trip-pricing-row {
    padding: 0.8rem;
    gap: 0.6rem;
  }

  .trips-grid {
    padding: 0 1rem;
    gap: 1.5rem;
    justify-items: center;
  }

  .trip-duration-group-row {
    flex-direction: column;
    gap: 0.6rem;
    padding: 0.8rem;
  }

  .trip-dates-row {
    padding: 0.8rem;
  }

  .price-display {
    font-size: 1.1rem;
  }

  .amount {
    font-size: 1.3rem;
  }

  .duration-display,
  .group-size-display {
    font-size: 0.8rem;
    padding: 0.5rem 0.8rem;
    width: 100%;
    text-align: center;
  }

  .meta-item {
    font-size: 0.8rem;
  }

  .meta-item i {
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding-top: 60px;
  }

  .trip-card {
    min-width: 85%;
    max-width: 88%;
    margin-left: auto;
    margin-right: auto;
    padding: 8px;
    min-height: 450px;
  }

  .carousel-container {
    height: 280px;
  }

  /* Even more compact for small mobile screens */
  .trip-title {
    padding: 0.6rem 0.4rem;
  }

  .location-name {
    font-size: 1.1rem;
    word-wrap: break-word;
    word-break: break-word;
  }

  .trip-pricing-row {
    padding: 0.4rem;
    gap: 0.3rem;
  }

  .trip-duration-group-row {
    padding: 0.4rem;
    gap: 0.3rem;
  }

  .trip-dates-row {
    padding: 0.4rem;
  }

  .trip-description {
    padding: 0.4rem;
  }

  .description-text {
    font-size: 0.8rem;
    line-height: 1.3;
  }

  .price-display {
    font-size: 0.85rem;
  }

  .duration-text, .group-size-text {
    font-size: 0.65rem;
  }

  /* Even smaller buttons for small mobile screens */
  .view-itinerary-btn-compact {
    padding: 0.3rem 0.6rem;
    font-size: 0.65rem;
    min-height: 24px;
    max-height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .book-now-btn,
  .customize-trip-btn {
    padding: 0.3rem 0.6rem;
    font-size: 0.65rem;
    min-height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }

  .view-itinerary-btn-compact i,
  .book-now-btn i,
  .customize-trip-btn i {
    font-size: 0.55rem;
  }

  .carousel-container {
    height: 180px;
  }

  .hero-navbar-content {
    padding: 0 0.8rem;
    min-height: 55px;
  }

  .hero-brand {
    font-size: 1rem;
  }

  .hero-logo {
    height: 65px;
  }

  .hero-content {
    padding: 1rem 0.8rem;
  }

  .hero-title {
    font-size: 1.8rem;
    line-height: 1.1;
  }

  .second-tagline-text {
    font-size: 1rem;
  }

  .hero-subtitle {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .stat-item {
    padding: 0.8rem 1rem;
    min-width: 100px;
    max-width: 180px;
  }

  .stat-number {
    font-size: 1.6rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .rating-icon {
    width: 70px;
    height: 70px;
    padding: 0.4rem;
  }

  .rating-text {
    font-size: 0.75rem;
  }

  .hero-btn {
    padding: 0.8rem 1.2rem;
    font-size: 0.9rem;
    max-width: 240px;
  }
}

/* Reviews Section Styles */
.reviews-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 12px;
  margin-top: 2rem;
  position: relative;
}

.reviews-scroll-container::-webkit-scrollbar {
  height: 8px;
}

.reviews-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.reviews-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.reviews-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.reviews-scroll-row {
  display: flex;
  gap: 1.5rem;
  min-width: max-content;
  padding: 8px 0;
}

.review-card-wrapper {
  flex-shrink: 0;
  width: 320px;
  min-height: 350px;
  max-height: 500px;
}

.review-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.8);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  cursor: pointer;
}

.review-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  border: 2px solid var(--primary-color);
}

/* Custom scrollbar for review cards */
.review-card::-webkit-scrollbar {
  width: 6px;
}

.review-card::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.review-card::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.review-card::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.review-header {
  margin-bottom: 1rem;
}

.reviewer-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--dark-color);
  margin-bottom: 0.5rem;
}

.rating {
  display: flex;
  gap: 0.2rem;
}

.rating i {
  color: var(--secondary-color);
  font-size: 1rem;
}

.review-text-container {
  margin-bottom: 1rem;
  flex: 1;
  overflow-y: auto;
}

.review-text {
  line-height: 1.5;
  color: var(--dark-color);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  /* Ensure emojis render correctly */
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "Android Emoji", "EmojiSymbols", "EmojiOne Mozilla", "Twemoji Mozilla", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  font-feature-settings: "liga" off;
  -webkit-font-feature-settings: "liga" off;
  -moz-font-feature-settings: "liga" off;
  text-rendering: optimizeLegibility;
}

.review-trip {
  font-size: 0.9rem;
  color: var(--primary-color);
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

/* Scrollable Media Container for Reviews */
.review-media {
  margin-bottom: 1rem;
}

.media-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
}

.media-scroll-container::-webkit-scrollbar {
  height: 6px;
}

.media-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.media-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.media-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.media-scroll-row {
  display: flex;
  gap: 0.75rem;
  min-width: max-content;
  padding: 4px 0;
}

.media-scroll-row .media-item {
  flex-shrink: 0;
  width: 250px;
  height: 250px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  border: 2px solid transparent;
}

.media-scroll-row .media-item:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-color);
}

.media-scroll-row .media-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  transition: background 0.3s ease;
}

.media-scroll-row .media-item:hover .video-play-overlay {
  background: rgba(0, 0, 0, 0.8);
}

/* Fixed Review Icon */
.fixed-review-icon {
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(135deg, #ef4444, #f87171);
  color: white;
  border-radius: 50px;
  padding: 15px 20px;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.fixed-review-icon:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 12px 35px rgba(239, 68, 68, 0.4);
  background: linear-gradient(135deg, #dc2626, #ef4444);
}

.fixed-review-icon:active {
  transform: translateY(-1px) scale(1.02);
}

.fixed-review-icon i {
  font-size: 18px;
  animation: pulse 2s infinite;
}

.review-icon-text {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Creative About Section Styles */
.about-section {
  padding: 8rem 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  position: relative;
  overflow: hidden;
  min-height: 100vh;
}

.about-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("@/assets/1.jpg") center center / cover no-repeat;
  opacity: 0.15;
  z-index: 1;
}

.floating-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  pointer-events: none;
}

.floating-element {
  position: absolute;
  font-size: 3rem;
  opacity: 0.3;
  animation: floatAround 20s infinite linear;
}

.element-1 {
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.element-2 {
  top: 20%;
  right: 15%;
  animation-delay: -4s;
}

.element-3 {
  bottom: 30%;
  left: 20%;
  animation-delay: -8s;
}

.element-4 {
  bottom: 20%;
  right: 25%;
  animation-delay: -12s;
}

.element-5 {
  top: 50%;
  left: 50%;
  animation-delay: -16s;
}

@keyframes floatAround {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  25% {
    transform: translateY(-20px) rotate(90deg);
  }
  50% {
    transform: translateY(-10px) rotate(180deg);
  }
  75% {
    transform: translateY(-30px) rotate(270deg);
  }
}

.about-content {
  position: relative;
  z-index: 3;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* Philosophy Card */
.philosophy-card {
  background: white;
  border-radius: 30px;
  padding: 4rem;
  text-align: center;
  margin-bottom: 6rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.philosophy-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1),
    rgba(16, 185, 129, 0.1)
  );
  z-index: -1;
}

.philosophy-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #2563eb, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  animation: pulse 2s infinite;
}

.philosophy-icon i {
  font-size: 2rem;
  color: white;
}

.philosophy-title {
  font-size: 3.5rem;
  font-weight: 300;
  font-style: italic;
  font-family: 'Lora', serif;
  color: #1e293b;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  line-height: 1.2;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #2563eb, #059669);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.philosophy-text p {
  color: #6b7280;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.philosophy-text p:last-child {
  margin-bottom: 0;
}

.philosophy-section-content {
  margin-bottom: 2.5rem;
}

.philosophy-section-content:last-child {
  margin-bottom: 0;
}

.philosophy-subtitle {
  font-size: 1.5rem;
  font-weight: 600;
  color: #b46428;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Philosophy Section */
.philosophy-section {
  padding: 6rem 0;
  background: linear-gradient(135deg, #f5f2ed 0%, #e8e0d6 50%, #d4c4b0 100%);
  position: relative;
  overflow: hidden;
}

.philosophy-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("@/assets/1.jpg") center center / cover no-repeat;
  opacity: 0.05;
  z-index: 1;
}

.philosophy-section .container {
  position: relative;
  z-index: 2;
}

.philosophy-section .philosophy-card {
  background: linear-gradient(135deg, #f5f2ed 0%, #e8e0d6 100%);
  border-radius: 30px;
  padding: 4rem;
  text-align: center;
  margin: 0 auto;
  max-width: 1000px;
  border: 1px solid rgba(180, 100, 40, 0.2);
  box-shadow: 0 20px 60px rgba(44, 36, 25, 0.15);
  position: relative;
  overflow: hidden;
}

.philosophy-section .philosophy-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(180, 100, 40, 0.1),
    rgba(120, 80, 30, 0.05)
  );
  z-index: -1;
}

.philosophy-section .philosophy-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #b46428, #78501e);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  animation: pulse 2s infinite;
}

.philosophy-section .philosophy-icon i {
  font-size: 2rem;
  color: white;
}

.philosophy-section .philosophy-title {
  font-size: 3.5rem;
  font-weight: 300;
  font-style: italic;
  color: #1e293b;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  line-height: 1.2;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #b46428, #78501e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.philosophy-section .philosophy-text p {
  color: #6b7280;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.philosophy-section .philosophy-text p:last-child {
  margin-bottom: 0;
}

/* Responsive Design for Philosophy Section */
@media (max-width: 768px) {
  .philosophy-section {
    padding: 3rem 0;
  }

  .philosophy-section .philosophy-card {
    padding: 2.5rem 1.5rem;
    border-radius: 20px;
  }

  .philosophy-section .philosophy-title {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }

  .philosophy-section .philosophy-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 1.5rem;
  }

  .philosophy-section .philosophy-icon i {
    font-size: 1.5rem;
  }

  .philosophy-section .philosophy-subtitle {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }

  .philosophy-section .philosophy-text p {
    font-size: 0.95rem;
    line-height: 1.6;
    margin-bottom: 1rem;
  }

  .philosophy-section-content {
    margin-bottom: 1.5rem;
  }
}

@media (max-width: 576px) {
  .philosophy-section {
    padding: 2.5rem 0;
  }

  .philosophy-section .philosophy-card {
    padding: 1.5rem 1rem;
    border-radius: 15px;
  }

  .philosophy-section .philosophy-title {
    font-size: 1.6rem;
    margin-bottom: 1rem;
  }

  .philosophy-section .philosophy-icon {
    width: 50px;
    height: 50px;
    margin-bottom: 1rem;
  }

  .philosophy-section .philosophy-icon i {
    font-size: 1.2rem;
  }

  .philosophy-section .philosophy-subtitle {
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
    letter-spacing: 0.5px;
  }

  .philosophy-section .philosophy-text p {
    font-size: 0.85rem;
    line-height: 1.5;
    margin-bottom: 0.75rem;
  }

  .philosophy-section-content {
    margin-bottom: 1.25rem;
  }
}

/* Custom Journeys Section */
.custom-journeys-section {
  padding: 6rem 0;
  background: linear-gradient(135deg, #f5f2ed 0%, #e8e0d6 50%, #d4c4b0 100%);
  position: relative;
  overflow: hidden;
}

.custom-journeys-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("@/assets/1.jpg") center center / cover no-repeat;
  opacity: 0.05;
  z-index: 1;
}

.custom-journeys-section .container {
  position: relative;
  z-index: 2;
}

.custom-journeys-section .section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.custom-journeys-section .section-title {
  font-size: 3rem;
  font-weight: 300;
  margin-bottom: 1rem;
  color: #1e293b;
}

.custom-journeys-section .title-accent {
  background: linear-gradient(135deg, #b46428, #78501e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 600;
}

.custom-journeys-section .section-subtitle {
  font-size: 1.2rem;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.custom-journeys-section .journey-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.custom-journeys-section .journey-card {
  background: white;
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.custom-journeys-section .journey-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.05),
    rgba(5, 150, 105, 0.05)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.custom-journeys-section .journey-card:hover::before {
  opacity: 1;
}

.custom-journeys-section .journey-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
  border-color: rgba(37, 99, 235, 0.2);
}

.custom-journeys-section .journey-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
}

.custom-journeys-section .journey-name {
  color: #1e293b;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
}

.custom-journeys-section .journey-card:hover .journey-name {
  color: #2563eb;
}

.custom-journeys-section .journey-description {
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
}

.custom-journeys-section .journey-card:hover .journey-description {
  color: #4b5563;
}

.custom-journeys-section .journey-overlay {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
}

.custom-journeys-section .journey-tag {
  background: linear-gradient(135deg, #2563eb, #059669);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.custom-journeys-section .journey-highlights {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.custom-journeys-section .highlight-item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background: white;
  padding: 1.2rem 2.5rem;
  border-radius: 30px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.custom-journeys-section .highlight-item:hover {
  background: linear-gradient(135deg, #2563eb, #059669);
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.custom-journeys-section .highlight-item:hover i,
.custom-journeys-section .highlight-item:hover span {
  color: white;
}

.custom-journeys-section .highlight-item i {
  color: #059669;
  font-size: 1.3rem;
  transition: color 0.3s ease;
}

.custom-journeys-section .highlight-item span {
  color: #1e293b;
  font-weight: 600;
  font-size: 1rem;
  transition: color 0.3s ease;
}

/* Responsive Design for Custom Journeys Section */
@media (max-width: 992px) {
  .custom-journeys-section .journey-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  .custom-journeys-section .section-title {
    font-size: 2.5rem;
  }

  .custom-journeys-section .journey-highlights {
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .custom-journeys-section {
    padding: 3rem 0;
  }

  .custom-journeys-section .section-header {
    margin-bottom: 2.5rem;
  }

  .custom-journeys-section .section-title {
    font-size: 1.8rem;
    margin-bottom: 0.75rem;
  }

  .custom-journeys-section .section-subtitle {
    font-size: 1rem;
  }

  .custom-journeys-section .journey-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .custom-journeys-section .journey-card {
    padding: 1rem;
    border-radius: 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    position: relative;
    padding-top: 2.5rem; /* Add top padding for tag */
  }

  .custom-journeys-section .journey-overlay {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 3;
  }

  .custom-journeys-section .journey-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    order: 1; /* Icon first */
  }

  .custom-journeys-section .journey-name {
    font-size: 1rem;
    margin-bottom: 0.4rem;
    order: 2; /* Name after icon */
    margin-top: 0;
  }

  .custom-journeys-section .journey-description {
    font-size: 0.85rem;
    line-height: 1.35;
    margin-bottom: 0.5rem;
    order: 3; /* Description last */
  }

  .custom-journeys-section .journey-tag {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
  }

  .custom-journeys-section .journey-highlights {
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .custom-journeys-section .highlight-item {
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    border-radius: 20px;
    gap: 0.5rem;
  }

  .custom-journeys-section .highlight-item i {
    font-size: 1rem;
  }

  .custom-journeys-section .highlight-item span {
    font-size: 0.85rem;
  }
}

@media (max-width: 576px) {
  .custom-journeys-section {
    padding: 2.5rem 0;
  }

  .custom-journeys-section .section-header {
    margin-bottom: 2rem;
  }

  .custom-journeys-section .section-title {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .custom-journeys-section .section-subtitle {
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .custom-journeys-section .journey-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .custom-journeys-section .journey-card {
    padding: 0.75rem;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    position: relative;
    padding-top: 2rem; /* Add top padding for tag */
  }

  .custom-journeys-section .journey-overlay {
    position: absolute;
    top: 0.4rem;
    right: 0.4rem;
    z-index: 3;
  }

  .custom-journeys-section .journey-icon {
    font-size: 1.75rem;
    margin-bottom: 0.4rem;
    order: 1; /* Icon first */
  }

  .custom-journeys-section .journey-name {
    font-size: 0.9rem;
    margin-bottom: 0.3rem;
    order: 2; /* Name after icon */
    margin-top: 0;
  }

  .custom-journeys-section .journey-description {
    font-size: 0.75rem;
    line-height: 1.3;
    margin-bottom: 0.4rem;
    order: 3; /* Description last */
  }

  .custom-journeys-section .journey-tag {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
  }

  .custom-journeys-section .journey-highlights {
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.75rem;
  }

  .custom-journeys-section .highlight-item {
    padding: 0.5rem 0.85rem;
    font-size: 0.75rem;
    border-radius: 15px;
    gap: 0.4rem;
  }

  .custom-journeys-section .highlight-item i {
    font-size: 0.9rem;
  }

  .custom-journeys-section .highlight-item span {
    font-size: 0.75rem;
  }
}

/* Journey Section */
.journey-section {
  margin-bottom: 6rem;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 3rem;
  font-weight: 300;
  font-style: italic;
  color: white;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff, #e2e8f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.title-underline {
  width: 100px;
  height: 3px;
  background: linear-gradient(90deg, #ef4444, #f59e0b, #10b981);
  margin: 0 auto;
  border-radius: 2px;
}

.journey-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.journey-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.journey-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1),
    rgba(16, 185, 129, 0.1)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.journey-card:hover::before {
  opacity: 1;
}

.journey-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4);
  border-color: rgba(59, 130, 246, 0.5);
}

.journey-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
}

.journey-name {
  color: white;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 2;
}

.journey-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
}

.journey-overlay {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
}

.journey-tag {
  background: linear-gradient(135deg, #ef4444, #f59e0b);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.journey-highlights {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.highlight-item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem 2rem;
  border-radius: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.highlight-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
}

.highlight-item i {
  color: #10b981;
  font-size: 1.2rem;
}

.highlight-item span {
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
}

/* Why Choose Us Section */
.why-choose-section {
  margin-bottom: 4rem;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.benefit-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  cursor: pointer;
}

.benefit-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1),
    rgba(16, 185, 129, 0.1)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.benefit-card:hover::before {
  opacity: 1;
}

.benefit-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  border-color: rgba(59, 130, 246, 0.5);
}

.benefit-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ef4444, #f59e0b);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}

.benefit-icon i {
  font-size: 1.5rem;
  color: white;
}

.benefit-content {
  flex: 1;
  position: relative;
  z-index: 2;
}

.benefit-title {
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.benefit-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
}

.benefit-number {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #ef4444, #f59e0b);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  z-index: 2;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .philosophy-title {
    font-size: 3rem;
  }

  .section-title {
    font-size: 2.5rem;
  }
}

@media (max-width: 992px) {
  .journey-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .journey-highlights {
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .about-section {
    padding: 6rem 0;
  }

  .philosophy-card {
    padding: 3rem 2rem;
  }

  .philosophy-title {
    font-size: 2.5rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .journey-grid {
    grid-template-columns: 1fr;
  }

  .journey-highlights {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .benefit-card {
    padding: 2rem;
  }
}

@media (max-width: 576px) {
  .philosophy-title {
    font-size: 2rem;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .philosophy-card {
    padding: 2rem 1.5rem;
  }

  .journey-card,
  .benefit-card {
    padding: 1.5rem;
  }

  .floating-element {
    font-size: 2rem;
  }
}

/* Why Travel With Us Section */
.why-travel-section {
  padding: 6rem 0;
  background: linear-gradient(135deg, #e8e0d6 0%, #d4c4b0 50%, #c4b5a0 100%);
  position: relative;
  overflow: hidden;
}

.why-travel-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("@/assets/1.jpg") center center / cover no-repeat;
  opacity: 0.05;
  z-index: 1;
}

.why-travel-section .container {
  position: relative;
  z-index: 2;
}

.why-travel-section .section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.why-travel-section .section-title {
  font-size: 3rem;
  font-weight: 300;
  margin-bottom: 1rem;
  color: #1e293b;
}

.why-travel-section .title-accent {
  background: linear-gradient(135deg, #b46428, #78501e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 600;
}

.why-travel-section .section-subtitle {
  font-size: 1.2rem;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.why-travel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
}

.why-card {
  background: linear-gradient(135deg, #f5f2ed 0%, #e8e0d6 100%);
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(44, 36, 25, 0.15);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  cursor: pointer;
  border: 1px solid rgba(180, 100, 40, 0.2);
}

.why-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(180, 100, 40, 0.1),
    rgba(120, 80, 30, 0.05)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.why-card:hover::before {
  opacity: 1;
}

.why-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 25px 60px rgba(44, 36, 25, 0.2);
  border-color: rgba(180, 100, 40, 0.4);
}

.why-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #b46428, #78501e);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
}

.why-card:hover .why-icon {
  transform: scale(1.1) rotate(5deg);
}

.why-icon i {
  font-size: 1.8rem;
  color: white;
}

.why-content {
  flex: 1;
  position: relative;
  z-index: 2;
}

.why-title {
  color: #1e293b;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  transition: color 0.3s ease;
}

.why-card:hover .why-title {
  color: #2563eb;
}

.why-description {
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  transition: color 0.3s ease;
}

.why-card:hover .why-description {
  color: #4b5563;
}

.why-number {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #2563eb, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
  z-index: 2;
  transition: all 0.3s ease;
}

.why-card:hover .why-number {
  transform: scale(1.1);
}

.why-travel-highlights {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
  margin-top: 2rem;
}

.why-travel-section .highlight-item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background: linear-gradient(135deg, #f5f2ed 0%, #e8e0d6 100%);
  padding: 1.2rem 2.5rem;
  border-radius: 30px;
  border: 1px solid rgba(180, 100, 40, 0.2);
  box-shadow: 0 5px 15px rgba(44, 36, 25, 0.1);
  transition: all 0.3s ease;
}

.why-travel-section .highlight-item:hover {
  background: linear-gradient(135deg, #b46428, #78501e);
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(44, 36, 25, 0.2);
}

.why-travel-section .highlight-item:hover i,
.why-travel-section .highlight-item:hover span {
  color: white;
}

.why-travel-section .highlight-item i {
  color: #b46428;
  font-size: 1.3rem;
  transition: color 0.3s ease;
}

.why-travel-section .highlight-item span {
  color: #1e293b;
  font-weight: 600;
  font-size: 1rem;
  transition: color 0.3s ease;
}

/* Responsive Design for Why Travel Section */
@media (max-width: 992px) {
  .why-travel-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  .why-travel-section .section-title {
    font-size: 2.5rem;
  }

  .why-travel-highlights {
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .why-travel-section {
    padding: 3rem 0;
  }

  .why-travel-section .section-header {
    margin-bottom: 2.5rem;
  }

  .why-travel-section .section-title {
    font-size: 1.8rem;
    margin-bottom: 0.75rem;
  }

  .why-travel-section .section-subtitle {
    font-size: 1rem;
  }

  .why-travel-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .why-card {
    padding: 1rem;
    border-radius: 15px;
    gap: 0.75rem;
    flex-direction: column;
    text-align: center;
  }

  .why-icon {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    flex-shrink: 0;
    margin: 0 auto;
  }

  .why-icon i {
    font-size: 1.2rem;
  }

  .why-title {
    font-size: 1rem;
    margin-bottom: 0.4rem;
  }

  .why-description {
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .why-travel-highlights {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }

  .why-travel-section .highlight-item {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 576px) {
  .why-travel-section {
    padding: 2.5rem 0;
  }

  .why-travel-section .section-header {
    margin-bottom: 2rem;
  }

  .why-travel-section .section-title {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .why-travel-section .section-subtitle {
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .why-travel-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .why-card {
    padding: 0.75rem;
    border-radius: 12px;
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }

  .why-icon {
    width: 45px;
    height: 45px;
    border-radius: 10px;
    margin: 0 auto;
  }

  .why-icon i {
    font-size: 1.1rem;
  }

  .why-title {
    font-size: 0.9rem;
    margin-bottom: 0.3rem;
  }

  .why-description {
    font-size: 0.75rem;
    line-height: 1.35;
  }

  .why-travel-section .highlight-item {
    padding: 0.6rem 1.25rem;
    font-size: 0.85rem;
  }
}

/* Responsive Design for Fixed Review Icon */
@media (max-width: 768px) {
  .fixed-review-icon {
    bottom: 20px;
    left: 20px;
    padding: 12px 16px;
    font-size: 13px;
  }

  .fixed-review-icon i {
    font-size: 16px;
  }

  .review-icon-text {
    font-size: 13px;
  }
}




@media (max-width: 480px) {
  .fixed-review-icon {
    bottom: 15px;
    left: 15px;
    padding: 10px 14px;
    font-size: 12px;
  }

  .fixed-review-icon i {
    font-size: 14px;
  }

  .review-icon-text {
    font-size: 12px;
  }
}
</style>
